import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Dialog,
  InputAdornment,
  IconButton,
  Link,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  AlertCircle,
  ArrowLeft,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Copy,
  Lightbulb,
  Trash2,
  X,
} from 'lucide-react';
import {
  addMonths,
  differenceInCalendarDays,
  endOfMonth,
  format,
  getDate,
  getDay,
  isAfter,
  isBefore,
  isSameDay,
  startOfDay,
  startOfMonth,
} from 'date-fns';
import dayjs from 'dayjs';
import { isPastScheduleDay } from '@/utils/appointments';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AppOutlinedTextField } from '@/components/shared/AppOutlinedTextField';
import { ClientNameLink } from '@/components/clients/ClientNameLink';
import appointmentDateIcon from '@/assets/images/appointments/appointment-date.svg';
import appointmentRequestReschedulePendingIcon from '@/assets/images/appointments/appointment-request-reschedule-pending.svg';
import trashBinTrashIcon from '@/assets/images/trash-bin-trash.svg';
import {
  AVAILABILITY_WEEKDAYS,
  AvailabilityWeekday,
  CreateRecurringBreakPayload,
  CreateTimeOffPayload,
  RecurringBreak as ApiRecurringBreak,
  TimeOff as ApiTimeOff,
  TimeOffConflict,
  UpdateWeeklyAvailabilitySchedulePayload,
  WeeklyAvailabilityDay,
  WeeklyAvailabilitySlot,
  createRecurringBreak,
  createTimeOff,
  deleteTimeOff,
  deleteRecurringBreak,
  getTimeOff,
  getWeeklyAvailabilitySchedule,
  getRecurringBreaks,
  updateRecurringBreak,
  updateWeeklyAvailabilitySchedule,
} from '@/lib/api/availability';
import ScheduleAppointmentDialog from '@/components/appointments/ScheduleAppointmentDialog';
import InlineFeedback from '@/components/shared/InlineFeedback';
import { cancelAppointment, mapAppointmentRowsFromApi } from '@/lib/api/appointments';
import type { Appointment } from '@/types';
import { extractErrorMessage, showErrorToast, showSuccessToast } from '@/utils/toast-utils';
import { formatTimezoneLabel } from '@/utils/timezone-utils';
import { getAvailabilitySaveTimezone } from '@/utils/appointments/appointment-timezone';

type AvailabilitySection = 'weekly' | 'breaks' | 'timeoff';

interface DaySchedule {
  id: AvailabilityWeekday;
  label: string;
  enabled: boolean;
  slots: WeeklyAvailabilitySlot[];
}

interface SlotDraft {
  id: string;
  start: string;
  end: string;
}

interface RecurringBreak {
  id: string;
  name: string;
  days: AvailabilityWeekday[];
  startTime: string;
  endTime: string;
}

interface TimeOffEntry {
  id: string;
  startDate: string;
  endDate: string;
  dateRange: string;
  note: string;
  daysLabel: string;
}

interface RescheduleAppointment {
  id: string;
  day: string;
  month: string;
  clientId?: string;
  clientName: string;
  title: string;
  meta: string;
  appointment: Appointment | null;
}

type WeekdayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

interface RecurringBreakDraft {
  name: string;
  repeatDays: WeekdayKey[];
  start: string;
  end: string;
}

interface TimeSelectProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
}

const DAY_LABEL_BY_ID: Record<AvailabilityWeekday, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

const DEFAULT_SLOTS: WeeklyAvailabilitySlot[] = [
  { startTime: '09:00', endTime: '13:00' },
  { startTime: '14:00', endTime: '17:30' },
];

const cloneSlots = (slots: WeeklyAvailabilitySlot[]): WeeklyAvailabilitySlot[] =>
  slots.map((slot) => ({ ...slot }));

const buildDefaultSchedule = (): DaySchedule[] =>
  AVAILABILITY_WEEKDAYS.map((dayId) => {
    const isWeekday = !['saturday', 'sunday'].includes(dayId);
    return {
      id: dayId,
      label: DAY_LABEL_BY_ID[dayId],
      enabled: isWeekday,
      slots: isWeekday ? cloneSlots(DEFAULT_SLOTS) : [],
    };
  });

const DEFAULT_SCHEDULE = buildDefaultSchedule();

const AVAILABILITY_TABS: Array<{ id: AvailabilitySection; label: string }> = [
  { id: 'weekly', label: 'Weekly Schedule' },
  { id: 'breaks', label: 'Breaks' },
  { id: 'timeoff', label: 'Time Off' },
];

const WEEKDAY_OPTIONS: Array<{ id: WeekdayKey; label: string }> = [
  { id: 'mon', label: 'Mon' },
  { id: 'tue', label: 'Tue' },
  { id: 'wed', label: 'Wed' },
  { id: 'thu', label: 'Thu' },
  { id: 'fri', label: 'Fri' },
  { id: 'sat', label: 'Sat' },
  { id: 'sun', label: 'Sun' },
];

const WEEKDAY_TO_AVAILABILITY: Record<WeekdayKey, AvailabilityWeekday> = {
  mon: 'monday',
  tue: 'tuesday',
  wed: 'wednesday',
  thu: 'thursday',
  fri: 'friday',
  sat: 'saturday',
  sun: 'sunday',
};

const AVAILABILITY_TO_WEEKDAY: Record<AvailabilityWeekday, WeekdayKey> = {
  monday: 'mon',
  tuesday: 'tue',
  wednesday: 'wed',
  thursday: 'thu',
  friday: 'fri',
  saturday: 'sat',
  sunday: 'sun',
};

const CALENDAR_WEEK_HEADERS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

const createSlotDraft = (): SlotDraft => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  start: '09:00',
  end: '10:00',
});

const createRecurringBreakDraft = (): RecurringBreakDraft => ({
  name: '',
  repeatDays: ['mon', 'thu'],
  start: '09:00',
  end: '09:30',
});

const formatRepeatDays = (days: WeekdayKey[]): string => {
  if (days.length === 0) return 'No days';

  const weekdayIds: WeekdayKey[] = ['mon', 'tue', 'wed', 'thu', 'fri'];
  const isWeekdayBlock = weekdayIds.every((dayId) => days.includes(dayId)) && days.length === 5;
  if (isWeekdayBlock) {
    return 'Mon - Fri';
  }

  return WEEKDAY_OPTIONS.filter((option) => days.includes(option.id))
    .map((option) => option.label)
    .join(', ');
};

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;

const toApiDateOnly = (dateValue: Date): string => format(dateValue, 'yyyy-MM-dd');

const BLOCK_DATES_SELECT_ERROR = 'Choose at least one date to block.';
const BLOCK_DATES_PAST_ERROR = 'Time off can only be added for today or future dates.';
const BLOCK_DATES_RANGE_ERROR = 'End date must be on or after the start date.';
const BLOCK_DATES_ALREADY_BLOCKED_ERROR = 'Selected dates include an existing blocked date.';

const normalizeDateOnly = (value: unknown): string => {
  const match = String(value || '').trim().match(DATE_ONLY_PATTERN);
  return match ? match[0] : '';
};

type BlockedDateRange = Pick<TimeOffEntry, 'startDate' | 'endDate'>;

const isDateInBlockedDateRanges = (dateValue: Date, blockedDateRanges: BlockedDateRange[]): boolean => {
  const dateKey = toApiDateOnly(startOfDay(dateValue));
  return blockedDateRanges.some((range) => dateKey >= range.startDate && dateKey <= range.endDate);
};

const rangeIntersectsBlockedDateRanges = (
  start: Date,
  end: Date,
  blockedDateRanges: BlockedDateRange[],
): boolean => {
  const selectedStart = toApiDateOnly(startOfDay(start));
  const selectedEnd = toApiDateOnly(startOfDay(end));

  return blockedDateRanges.some((range) => selectedStart <= range.endDate && selectedEnd >= range.startDate);
};

const getBlockDatesValidationError = (
  start: Date | null,
  end: Date | null,
  blockedDateRanges: BlockedDateRange[],
): string => {
  if (!start) return '';

  const normalizedStart = startOfDay(start);
  if (isPastScheduleDay(normalizedStart)) {
    return BLOCK_DATES_PAST_ERROR;
  }

  if (!end && isDateInBlockedDateRanges(normalizedStart, blockedDateRanges)) {
    return BLOCK_DATES_ALREADY_BLOCKED_ERROR;
  }

  if (!end) return '';

  const normalizedEnd = startOfDay(end);
  if (isPastScheduleDay(normalizedEnd)) {
    return BLOCK_DATES_PAST_ERROR;
  }

  if (isBefore(normalizedEnd, normalizedStart)) {
    return BLOCK_DATES_RANGE_ERROR;
  }

  if (rangeIntersectsBlockedDateRanges(normalizedStart, normalizedEnd, blockedDateRanges)) {
    return BLOCK_DATES_ALREADY_BLOCKED_ERROR;
  }

  return '';
};

const parseDateOnly = (value: string): Date | null => {
  const match = normalizeDateOnly(value).match(DATE_ONLY_PATTERN);
  if (!match) return null;

  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day));
};

const formatDateOnlyLabel = (value: string): string => {
  const parsedDate = parseDateOnly(value);
  return parsedDate ? format(parsedDate, 'dd MMM yyyy') : value;
};

const buildTimeOffDaysLabel = (startDate: string, endDate: string): string => {
  const parsedStart = parseDateOnly(startDate);
  const parsedEnd = parseDateOnly(endDate);
  const totalDays = parsedStart && parsedEnd ? Math.max(1, differenceInCalendarDays(parsedEnd, parsedStart) + 1) : 1;
  return `${totalDays} day${totalDays > 1 ? 's' : ''}`;
};

const mapApiTimeOffEntry = (entry: ApiTimeOff, index: number): TimeOffEntry | null => {
  const startDate = normalizeDateOnly(entry.startDate);
  const endDate = normalizeDateOnly(entry.endDate) || startDate;

  if (!startDate || !endDate) {
    return null;
  }

  const id = String(entry.id || entry._id || `${startDate}-${endDate}-${index}`);
  const note = String(entry.reason || '').trim() || 'Blocked Date';

  return {
    id,
    startDate,
    endDate,
    dateRange: `${formatDateOnlyLabel(startDate)} - ${formatDateOnlyLabel(endDate)}`,
    note,
    daysLabel: buildTimeOffDaysLabel(startDate, endDate),
  };
};

const formatTimeTo12Hour = (value: unknown): string => {
  const normalized = String(value || '').trim().slice(0, 5);
  const match = normalized.match(/^([01]\d|2[0-3]):([0-5]\d)$/);
  if (!match) return normalized;

  const hours = Number(match[1]);
  const minutes = match[2];
  const suffix = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  return `${hour12}:${minutes} ${suffix}`;
};

const formatTimeValuesInMessage = (message: string): string => {
  const withRanges = message.replace(
    /\b(([01]\d|2[0-3]):[0-5]\d)\s*-\s*(([01]\d|2[0-3]):[0-5]\d)\b/g,
    (_match, startTime: string, _startHour: string, endTime: string) =>
      `${formatTimeTo12Hour(startTime)} - ${formatTimeTo12Hour(endTime)}`
  );

  return withRanges.replace(
    /\b([01]\d|2[0-3]):[0-5]\d\b(?!\s*(?:AM|PM)\b)/gi,
    (timeValue) => formatTimeTo12Hour(timeValue)
  );
};

const formatSlotLabel = (slot: WeeklyAvailabilitySlot): string =>
  `${formatTimeTo12Hour(slot.startTime)} - ${formatTimeTo12Hour(slot.endTime)}`;

const formatRecurringBreakSubtitle = (days: AvailabilityWeekday[] | undefined, startTime: string, endTime: string): string => {
  const normalizedDays = days || [];
  const timeRangeLabel = `${formatTimeTo12Hour(startTime)} - ${formatTimeTo12Hour(endTime)}`;
  if (normalizedDays.length === 0) {
    return timeRangeLabel;
  }

  const uiDays = normalizedDays.map((dayId) => AVAILABILITY_TO_WEEKDAY[dayId]);
  return `${formatRepeatDays(uiDays)} ${timeRangeLabel}`;
};

const normalizeTime = (value: unknown): string => String(value || '').trim().slice(0, 5);

const isValidTime = (value: string): boolean => /^([01]\d|2[0-3]):[0-5]\d$/.test(value);

const timeToMinutes = (value: string): number => {
  const [hours, minutes] = value.split(':').map(Number);
  return hours * 60 + minutes;
};

const normalizeSlot = (slot: WeeklyAvailabilitySlot): WeeklyAvailabilitySlot => ({
  startTime: normalizeTime(slot.startTime),
  endTime: normalizeTime(slot.endTime),
});

const isValidSlot = (slot: WeeklyAvailabilitySlot): boolean => {
  const normalizedSlot = normalizeSlot(slot);
  return (
    isValidTime(normalizedSlot.startTime) &&
    isValidTime(normalizedSlot.endTime) &&
    timeToMinutes(normalizedSlot.startTime) < timeToMinutes(normalizedSlot.endTime)
  );
};

const dedupeSlotsByTime = (slots: WeeklyAvailabilitySlot[]): WeeklyAvailabilitySlot[] => {
  const seen = new Set<string>();
  return slots.filter((slot) => {
    const normalizedSlot = normalizeSlot(slot);
    const key = `${normalizedSlot.startTime}-${normalizedSlot.endTime}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const TIME_SELECT_HOUR_OPTIONS = Array.from({ length: 12 }, (_, index) => String(index + 1));
const TIME_SELECT_MINUTE_OPTIONS = Array.from({ length: 60 }, (_, index) => String(index).padStart(2, '0'));
const TIME_SELECT_PERIOD_OPTIONS = ['AM', 'PM'] as const;

type TimeSelectPeriod = (typeof TIME_SELECT_PERIOD_OPTIONS)[number];

const parseTimeTo12HourParts = (
  value: string,
  fallback: { hour12: string; minute: string; period: TimeSelectPeriod } = { hour12: '9', minute: '00', period: 'AM' },
) => {
  const normalized = normalizeTime(value);
  if (!isValidTime(normalized)) {
    return fallback;
  }

  const [hours24, minutes] = normalized.split(':').map(Number);
  const period: TimeSelectPeriod = hours24 >= 12 ? 'PM' : 'AM';
  const hour12 = String(hours24 % 12 || 12);

  return {
    hour12,
    minute: String(minutes).padStart(2, '0'),
    period,
  };
};

const build24HourTime = (hour12: string, minute: string, period: TimeSelectPeriod): string => {
  const hourNumber = Number.parseInt(hour12, 10);
  const minuteNumber = Number.parseInt(minute, 10);
  const normalizedHour12 = Number.isFinite(hourNumber) && hourNumber >= 1 && hourNumber <= 12 ? hourNumber : 12;
  const normalizedMinute = Number.isFinite(minuteNumber) && minuteNumber >= 0 && minuteNumber <= 59 ? minuteNumber : 0;

  let hours24 = normalizedHour12 % 12;
  if (period === 'PM') {
    hours24 += 12;
  }

  return `${String(hours24).padStart(2, '0')}:${String(normalizedMinute).padStart(2, '0')}`;
};

const buildScheduleFromApiDays = (apiDays: WeeklyAvailabilityDay[]): DaySchedule[] => {
  const apiDayById = new Map(apiDays.map((day) => [day.day, day]));

  return AVAILABILITY_WEEKDAYS.map((dayId) => {
    const apiDay = apiDayById.get(dayId);
    const isEnabled = Boolean(apiDay?.isEnabled);
    const slots = dedupeSlotsByTime((apiDay?.slots || []).map(normalizeSlot).filter(isValidSlot));

    return {
      id: dayId,
      label: DAY_LABEL_BY_ID[dayId],
      enabled: isEnabled,
      slots,
    };
  });
};

const buildWeeklySchedulePayload = (schedule: DaySchedule[], timezone: string) => ({
  timezone,
  days: AVAILABILITY_WEEKDAYS.map((dayId) => {
    const day = schedule.find((scheduleDay) => scheduleDay.id === dayId);
    const isEnabled = Boolean(day?.enabled);

    return {
      day: dayId,
      isEnabled,
      slots: dedupeSlotsByTime(day?.slots || []).map(normalizeSlot),
    };
  }),
});

const buildMonthDateCells = (monthDate: Date): Array<Date | null> => {
  const monthStartDate = startOfMonth(monthDate);
  const totalDaysInMonth = getDate(endOfMonth(monthDate));
  const firstDayIndexMondayFirst = (getDay(monthStartDate) + 6) % 7;

  const cells: Array<Date | null> = [];
  for (let index = 0; index < firstDayIndexMondayFirst; index += 1) {
    cells.push(null);
  }

  for (let day = 1; day <= totalDaysInMonth; day += 1) {
    cells.push(new Date(monthStartDate.getFullYear(), monthStartDate.getMonth(), day));
  }

  return cells;
};

const timeSelectFieldSx = {
  width: '100%',
  height: 40,
  borderRadius: '10px',
  border: '1px solid #D6E0E9',
  bgcolor: '#fff',
  display: 'flex',
  alignItems: 'center',
  gap: 0.35,
  px: 1,
  '&:focus-within': {
    borderColor: '#59BADB',
  },
};

const timeSelectNativeSx = {
  flex: 1,
  minWidth: 0,
  border: 'none',
  outline: 'none',
  bgcolor: 'transparent',
  color: '#1F2B34',
  fontSize: '0.94rem',
  lineHeight: 1,
  cursor: 'pointer',
  appearance: 'none',
  textAlign: 'center',
  '&:disabled': {
    cursor: 'not-allowed',
    opacity: 0.6,
  },
};

const TimeSelect: React.FC<TimeSelectProps> = ({ value, onChange, disabled = false, required = false }) => {
  const { hour12, minute, period } = parseTimeTo12HourParts(value);

  const handlePartChange = (nextHour12: string, nextMinute: string, nextPeriod: TimeSelectPeriod) => {
    onChange(build24HourTime(nextHour12, nextMinute, nextPeriod));
  };

  return (
    <Box role="group" aria-label="Time" sx={timeSelectFieldSx}>
      <Box
        component="select"
        aria-label="Hour"
        disabled={disabled}
        required={required}
        value={hour12}
        onChange={(event) => handlePartChange(event.target.value, minute, period)}
        sx={{ ...timeSelectNativeSx, flex: 0.85 }}
      >
        {TIME_SELECT_HOUR_OPTIONS.map((hourOption) => (
          <option key={hourOption} value={hourOption}>
            {hourOption}
          </option>
        ))}
      </Box>
      <Typography component="span" sx={{ color: '#667380', fontSize: '0.94rem', userSelect: 'none' }}>
        :
      </Typography>
      <Box
        component="select"
        aria-label="Minute"
        disabled={disabled}
        required={required}
        value={minute}
        onChange={(event) => handlePartChange(hour12, event.target.value, period)}
        sx={{ ...timeSelectNativeSx, flex: 1.1 }}
      >
        {TIME_SELECT_MINUTE_OPTIONS.map((minuteOption) => (
          <option key={minuteOption} value={minuteOption}>
            {minuteOption}
          </option>
        ))}
      </Box>
      <Box
        component="select"
        aria-label="AM or PM"
        disabled={disabled}
        required={required}
        value={period}
        onChange={(event) => handlePartChange(hour12, minute, event.target.value as TimeSelectPeriod)}
        sx={{ ...timeSelectNativeSx, flex: 1.15, fontWeight: 600 }}
      >
        {TIME_SELECT_PERIOD_OPTIONS.map((periodOption) => (
          <option key={periodOption} value={periodOption}>
            {periodOption}
          </option>
        ))}
      </Box>
    </Box>
  );
};

const asPlainRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
};

const readDisplayValue = (value: unknown): string => {
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value).trim();
  }

  return '';
};

const readFirstDisplayValue = (
  records: Array<Record<string, unknown> | null>,
  keys: string[],
): string => {
  for (const record of records) {
    if (!record) continue;

    for (const key of keys) {
      const value = readDisplayValue(record[key]);
      if (value) return value;
    }
  }

  return '';
};

const parseDateTimeValue = (value: unknown): Date | null => {
  const stringValue = readDisplayValue(value);
  if (!stringValue) return null;

  const parsed = new Date(stringValue);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed;
  }

  return parseDateOnly(stringValue);
};

const buildDateTimeFromDateAndTime = (dateValue: unknown, timeValue: unknown): Date | null => {
  const parsedDate = parseDateTimeValue(dateValue);
  const normalizedTime = normalizeTime(timeValue);

  if (!parsedDate || !isValidTime(normalizedTime)) {
    return null;
  }

  const [hours, minutes] = normalizedTime.split(':').map(Number);
  const dateTime = new Date(parsedDate);
  dateTime.setHours(hours, minutes, 0, 0);
  return dateTime;
};

const getTimeOffConflictCount = (timeOff: ApiTimeOff): number => {
  const record = asPlainRecord(timeOff);
  const parsedCount = Number(record?.conflictCount ?? record?.conflict_count ?? 0);
  const count = Number.isFinite(parsedCount) ? Math.max(0, Math.floor(parsedCount)) : 0;
  const conflictsCount = Array.isArray(timeOff.conflicts) ? timeOff.conflicts.length : 0;

  return Math.max(count, conflictsCount);
};

const getTimeOffId = (timeOff: ApiTimeOff): string => {
  const id = timeOff.id || timeOff._id;
  return id ? String(id) : '';
};

const formatAppointmentCount = (count: number): string =>
  `${count} appointment${count === 1 ? '' : 's'}`;

const getConflictDate = (
  conflict: TimeOffConflict,
  fallbackDate: Date | null,
): Date | null => {
  const conflictRecord = asPlainRecord(conflict);
  const appointmentRecord =
    asPlainRecord(conflictRecord?.appointment) ||
    asPlainRecord(conflictRecord?.appointmentDetails) ||
    conflictRecord;
  const records = [conflictRecord, appointmentRecord];
  const dateValue = readFirstDisplayValue(records, ['date', 'appointmentDate', 'appointment_date']);
  const startTimeValue = readFirstDisplayValue(records, ['startTime', 'startTimeISO', 'start_time', 'startsAt', 'starts_at']);

  return (
    buildDateTimeFromDateAndTime(dateValue, startTimeValue) ||
    parseDateTimeValue(startTimeValue) ||
    parseDateTimeValue(dateValue) ||
    fallbackDate
  );
};

const getConflictClientName = (conflict: TimeOffConflict, fallbackIndex: number): string => {
  const conflictRecord = asPlainRecord(conflict);
  const appointmentRecord =
    asPlainRecord(conflictRecord?.appointment) ||
    asPlainRecord(conflictRecord?.appointmentDetails) ||
    conflictRecord;
  const clientRecord =
    asPlainRecord(conflictRecord?.client) ||
    asPlainRecord(conflictRecord?.clientDetails) ||
    asPlainRecord(appointmentRecord?.client);
  const directName = readFirstDisplayValue(
    [conflictRecord, appointmentRecord, clientRecord],
    ['clientName', 'client_name', 'fullName', 'full_name', 'name'],
  );

  if (directName) return directName;

  const firstName = readFirstDisplayValue([clientRecord], ['firstName', 'first_name']);
  const lastName = readFirstDisplayValue([clientRecord], ['lastName', 'last_name']);
  const fullName = `${firstName} ${lastName}`.trim();
  if (fullName) return fullName;

  return readFirstDisplayValue([clientRecord], ['email']) || `Appointment ${fallbackIndex + 1}`;
};

const getConflictTitle = (conflict: TimeOffConflict): string => {
  const conflictRecord = asPlainRecord(conflict);
  const appointmentRecord =
    asPlainRecord(conflictRecord?.appointment) ||
    asPlainRecord(conflictRecord?.appointmentDetails) ||
    conflictRecord;

  return readFirstDisplayValue(
    [conflictRecord, appointmentRecord],
    ['title', 'type', 'appointmentTitle', 'appointment_title'],
  ) || 'Scheduled Appointment';
};

const getConflictModeLabel = (conflict: TimeOffConflict): string => {
  const conflictRecord = asPlainRecord(conflict);
  const appointmentRecord =
    asPlainRecord(conflictRecord?.appointment) ||
    asPlainRecord(conflictRecord?.appointmentDetails) ||
    conflictRecord;
  const mode = readFirstDisplayValue(
    [conflictRecord, appointmentRecord],
    ['appointmentType', 'appointment_type', 'mode', 'location'],
  ).toLowerCase();

  return mode.includes('offline') || mode.includes('person') ? 'Offline' : 'Online';
};

const getConflictAppointmentForEdit = (
  conflict: TimeOffConflict,
  appointmentId: string,
  startDate: Date | null,
): Appointment | null => {
  if (!appointmentId || !startDate) return null;

  const conflictRecord = asPlainRecord(conflict);
  const appointmentRecord =
    asPlainRecord(conflictRecord?.appointment) ||
    asPlainRecord(conflictRecord?.appointmentDetails) ||
    null;
  const clientRecord =
    asPlainRecord(conflictRecord?.client) ||
    asPlainRecord(conflictRecord?.clientDetails) ||
    asPlainRecord(appointmentRecord?.client);
  const clientId = readFirstDisplayValue(
    [appointmentRecord, conflictRecord, clientRecord],
    ['clientId', 'client_id', 'clientID', 'client'],
  );

  if (!clientId) return null;

  const rawAppointment = {
    ...(appointmentRecord || {}),
    id: appointmentId,
    clientId,
    title: getConflictTitle(conflict),
    startTime: readFirstDisplayValue([appointmentRecord, conflictRecord], ['startTime', 'startTimeISO', 'start_time', 'startsAt', 'starts_at']) || startDate.toISOString(),
    endTime: readFirstDisplayValue([appointmentRecord, conflictRecord], ['endTime', 'endTimeISO', 'end_time', 'endsAt', 'ends_at']),
    appointmentType: readFirstDisplayValue([appointmentRecord, conflictRecord], ['appointmentType', 'appointment_type', 'mode']) || undefined,
    location: readFirstDisplayValue([appointmentRecord, conflictRecord], ['location']) || undefined,
    timezone: readFirstDisplayValue([appointmentRecord, conflictRecord], ['timezone']) || undefined,
    description: readFirstDisplayValue([appointmentRecord, conflictRecord], ['description', 'notes']) || undefined,
  };

  return mapAppointmentRowsFromApi([rawAppointment])[0] || null;
};

const buildRescheduleAppointments = (
  timeOff: ApiTimeOff,
  conflictCount: number,
  timeOffEntry: TimeOffEntry | null,
): RescheduleAppointment[] => {
  const fallbackDate = parseDateOnly(timeOffEntry?.startDate || normalizeDateOnly(timeOff.startDate));
  const conflicts = Array.isArray(timeOff.conflicts) ? timeOff.conflicts : [];
  const mappedConflicts = conflicts.map((conflict, index) => {
    const conflictRecord = asPlainRecord(conflict);
    const appointmentRecord =
      asPlainRecord(conflictRecord?.appointment) ||
      asPlainRecord(conflictRecord?.appointmentDetails) ||
      conflictRecord;
    const startDate = getConflictDate(conflict, fallbackDate);
    const appointmentId = readFirstDisplayValue(
      [conflictRecord, appointmentRecord],
      ['appointmentId', 'appointment_id', 'id', '_id'],
    );
    const appointment = getConflictAppointmentForEdit(conflict, appointmentId, startDate);

    return {
      id: appointmentId || `time-off-conflict-${index + 1}`,
      day: startDate ? format(startDate, 'dd') : '--',
      month: startDate ? format(startDate, 'MMM').toUpperCase() : '---',
      clientId: appointment?.clientId ? String(appointment.clientId) : undefined,
      clientName: getConflictClientName(conflict, index),
      title: getConflictTitle(conflict),
      meta: `${startDate ? format(startDate, 'h:mm a') : 'Time TBD'} · ${getConflictModeLabel(conflict)}`,
      appointment,
    };
  });

  const missingCount = Math.max(0, conflictCount - mappedConflicts.length);
  const fallbackAppointments: RescheduleAppointment[] = Array.from({ length: missingCount }, (_, index) => {
    const appointmentNumber = mappedConflicts.length + index + 1;

    return {
      id: `time-off-conflict-fallback-${appointmentNumber}`,
      day: fallbackDate ? format(fallbackDate, 'dd') : '--',
      month: fallbackDate ? format(fallbackDate, 'MMM').toUpperCase() : '---',
      clientName: `Appointment ${appointmentNumber}`,
      title: 'Scheduled Appointment',
      meta: timeOffEntry?.dateRange || 'During time off',
      appointment: null,
    };
  });

  return [...mappedConflicts, ...fallbackAppointments];
};

const AvailabilityView: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('section');
  const initialSection: AvailabilitySection =
    tabParam === 'weekly' || tabParam === 'breaks' || tabParam === 'timeoff' ? tabParam : 'weekly';
  const [activeSection, setActiveSection] = useState<AvailabilitySection>(initialSection);
  const [schedule, setSchedule] = useState<DaySchedule[]>(DEFAULT_SCHEDULE);
  const [isLoadingSchedule, setIsLoadingSchedule] = useState(true);
  const [isSavingSchedule, setIsSavingSchedule] = useState(false);
  const [isToggleSavingSchedule, setIsToggleSavingSchedule] = useState(false);
  const [scheduleError, setScheduleError] = useState('');
  const [scheduleMutationError, setScheduleMutationError] = useState('');
  const [scheduleTimezone, setScheduleTimezone] = useState('');
  const [slotModalDayId, setSlotModalDayId] = useState<AvailabilityWeekday | null>(null);
  const [slotDrafts, setSlotDrafts] = useState<SlotDraft[]>([]);
  const [slotModalError, setSlotModalError] = useState('');
  const [isCopyDaySelectorOpen, setIsCopyDaySelectorOpen] = useState(false);
  const [copyTargetDayIds, setCopyTargetDayIds] = useState<AvailabilityWeekday[]>([]);
  const [recurringBreaks, setRecurringBreaks] = useState<RecurringBreak[]>([]);
  const [timeOffEntries, setTimeOffEntries] = useState<TimeOffEntry[]>([]);
  const [isLoadingTimeOff, setIsLoadingTimeOff] = useState(false);
  const [isSavingTimeOff, setIsSavingTimeOff] = useState(false);
  const [timeOffError, setTimeOffError] = useState('');
  const [isAddRecurringBreakOpen, setIsAddRecurringBreakOpen] = useState(false);
  const [recurringBreakDraft, setRecurringBreakDraft] = useState<RecurringBreakDraft>(() => createRecurringBreakDraft());
  const [recurringBreakSubmitError, setRecurringBreakSubmitError] = useState('');
  const [editingRecurringBreakId, setEditingRecurringBreakId] = useState<string | null>(null);
  const [isBlockDatesOpen, setIsBlockDatesOpen] = useState(false);
  const [blockDatesMonth, setBlockDatesMonth] = useState<Date>(() => new Date());
  const [blockStartDate, setBlockStartDate] = useState<Date | null>(null);
  const [blockEndDate, setBlockEndDate] = useState<Date | null>(null);
  const [blockReason, setBlockReason] = useState('');
  const [blockDatesSubmitError, setBlockDatesSubmitError] = useState('');
  const [isAppointmentConflictOpen, setIsAppointmentConflictOpen] = useState(false);
  const [appointmentConflictCount, setAppointmentConflictCount] = useState(0);
  const [isRescheduleSessionOpen, setIsRescheduleSessionOpen] = useState(false);
  const [rescheduleAppointments, setRescheduleAppointments] = useState<RescheduleAppointment[]>([]);
  const [pendingTimeOffPayload, setPendingTimeOffPayload] = useState<CreateTimeOffPayload | null>(null);
  const [timeOffConflictError, setTimeOffConflictError] = useState('');
  const [isScheduleAppointmentOpen, setIsScheduleAppointmentOpen] = useState(false);
  const [rescheduleAppointmentId, setRescheduleAppointmentId] = useState<string | null>(null);
  const [cancellingRescheduleId, setCancellingRescheduleId] = useState('');

  const activeDay = slotModalDayId ? schedule.find((day) => day.id === slotModalDayId) || null : null;
  const blockDatesCalendarCells = useMemo(() => buildMonthDateCells(blockDatesMonth), [blockDatesMonth]);
  const blockDatesValidationError = useMemo(
    () => getBlockDatesValidationError(blockStartDate, blockEndDate, timeOffEntries),
    [blockStartDate, blockEndDate, timeOffEntries],
  );
  const blockDatesInlineError = blockDatesValidationError || blockDatesSubmitError;
  const isBlockedDateAlreadySaved = useCallback(
    (dateValue: Date) => isDateInBlockedDateRanges(dateValue, timeOffEntries),
    [timeOffEntries],
  );
  const shouldDisableBlockStartDate = useCallback(
    (value: dayjs.Dayjs) => Boolean(value?.isValid() && isBlockedDateAlreadySaved(value.toDate())),
    [isBlockedDateAlreadySaved],
  );
  const shouldDisableBlockEndDate = useCallback(
    (value: dayjs.Dayjs) => {
      if (!value?.isValid()) return false;

      const dateValue = startOfDay(value.toDate());
      if (isBlockedDateAlreadySaved(dateValue)) return true;

      return Boolean(
        blockStartDate &&
          !isBefore(dateValue, blockStartDate) &&
          rangeIntersectsBlockedDateRanges(blockStartDate, dateValue, timeOffEntries),
      );
    },
    [blockStartDate, isBlockedDateAlreadySaved, timeOffEntries],
  );
  /** Save modal — disables slot actions to avoid conflicting edits */
  const isWeeklyScheduleBusy = isLoadingSchedule || isSavingSchedule;
  /** Day on/off toggle — only switches wait; slot buttons stay stable */
  const isWeeklySwitchBusy = isWeeklyScheduleBusy || isToggleSavingSchedule;
  const isTimeOffBusy = isLoadingTimeOff || isSavingTimeOff;
  const visibleConflictCount = rescheduleAppointments.length || appointmentConflictCount;

  const loadWeeklySchedule = useCallback(async () => {
    setIsLoadingSchedule(true);
    setScheduleError('');

    try {
      const weeklySchedule = await getWeeklyAvailabilitySchedule();
      setSchedule(buildScheduleFromApiDays(weeklySchedule.days));
      setScheduleTimezone(weeklySchedule.timezone?.trim() || getAvailabilitySaveTimezone());
      setScheduleMutationError('');
    } catch (error) {
      const message = formatTimeValuesInMessage(extractErrorMessage(error));
      setScheduleError(message);
      showErrorToast(error, 'Failed to load availability');
    } finally {
      setIsLoadingSchedule(false);
    }
  }, []);

  const loadRecurringBreaks = useCallback(async () => {
    try {
      const breaks = await getRecurringBreaks();
      const mappedBreaks: RecurringBreak[] = breaks.map((breakItem: ApiRecurringBreak) => ({
        id: breakItem.id,
        name: breakItem.name,
        days: breakItem.days || [],
        startTime: normalizeTime(breakItem.startTime),
        endTime: normalizeTime(breakItem.endTime),
      }));
      setRecurringBreaks(mappedBreaks);
    } catch (error) {
      showErrorToast(error, 'Failed to load recurring breaks');
    }
  }, []);

  const loadTimeOffEntries = useCallback(async () => {
    setIsLoadingTimeOff(true);
    setTimeOffError('');

    try {
      const entries = await getTimeOff();
      const mappedEntries = entries
        .map((entry, index) => mapApiTimeOffEntry(entry, index))
        .filter((entry): entry is TimeOffEntry => Boolean(entry));

      setTimeOffEntries(mappedEntries);
    } catch (error) {
      const message = formatTimeValuesInMessage(extractErrorMessage(error));
      setTimeOffError(message);
      showErrorToast(error, 'Failed to load time off');
    } finally {
      setIsLoadingTimeOff(false);
    }
  }, []);

  useEffect(() => {
    const sectionParam = searchParams.get('section');
    if (sectionParam !== activeSection) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('section', activeSection);
      setSearchParams(nextParams, { replace: true });
    }
  }, [activeSection, searchParams, setSearchParams]);

  useEffect(() => {
    if (activeSection === 'weekly') {
      void loadWeeklySchedule();
      return;
    }

    if (activeSection === 'breaks') {
      void loadRecurringBreaks();
      return;
    }

    if (activeSection === 'timeoff') {
      void loadTimeOffEntries();
    }
  }, [activeSection, loadWeeklySchedule, loadRecurringBreaks, loadTimeOffEntries]);

  const openAddSlotsModal = (dayId: AvailabilityWeekday) => {
    const day = schedule.find((scheduleDay) => scheduleDay.id === dayId);
    const uniqueSlots = day ? dedupeSlotsByTime(day.slots) : [];
    const existingDrafts: SlotDraft[] =
      uniqueSlots.length > 0
        ? uniqueSlots.map((slot) => ({
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            start: normalizeTime(slot.startTime),
            end: normalizeTime(slot.endTime),
          }))
        : [createSlotDraft()];

    setSlotModalDayId(dayId);
    setSlotDrafts(existingDrafts);
    setSlotModalError('');
    setIsCopyDaySelectorOpen(false);
    setCopyTargetDayIds([]);
  };

  const closeAddSlotsModal = () => {
    setSlotModalDayId(null);
    setSlotDrafts([]);
    setSlotModalError('');
    setIsCopyDaySelectorOpen(false);
    setCopyTargetDayIds([]);
  };

  const resolveSchedulePayloadTimezone = () => getAvailabilitySaveTimezone();

  const toggleDay = async (dayId: string, enabled: boolean) => {
    const previousSchedule = schedule;
    const nextSchedule = schedule.map((day) => {
      if (day.id !== dayId) return day;
      return { ...day, enabled };
    });

    setSchedule(nextSchedule);
    setIsToggleSavingSchedule(true);
    try {
      const payload = buildWeeklySchedulePayload(nextSchedule, resolveSchedulePayloadTimezone());
      await updateWeeklyAvailabilitySchedule(payload);
      setScheduleError('');
      setScheduleMutationError('');
    } catch (error) {
      setSchedule(previousSchedule);
      showErrorToast(error, 'Failed to update availability');
    } finally {
      setIsToggleSavingSchedule(false);
    }
  };

  const removeExistingSlot = async (dayId: string, slotIndex: number) => {
    const previousSchedule = schedule;
    const nextSchedule = schedule.map((day) => {
      if (day.id !== dayId) return day;
      return {
        ...day,
        slots: day.slots.filter((_, index) => index !== slotIndex),
      };
    });

    setSchedule(nextSchedule);
    setIsSavingSchedule(true);
    setScheduleMutationError('');
    try {
      const payload = buildWeeklySchedulePayload(nextSchedule, resolveSchedulePayloadTimezone());
      await updateWeeklyAvailabilitySchedule(payload);
      setSchedule(buildScheduleFromApiDays(payload.days));
      setScheduleError('');
      setScheduleMutationError('');
      void loadWeeklySchedule();
    } catch (error) {
      const message = formatTimeValuesInMessage(extractErrorMessage(error));
      setSchedule(previousSchedule);
      setScheduleMutationError(message);
      showErrorToast(error, 'Failed to save availability');
    } finally {
      setIsSavingSchedule(false);
    }
  };

  const addModalDraft = () => {
    setSlotModalError('');
    setSlotDrafts((prev) => [...prev, createSlotDraft()]);
  };

  const updateModalDraft = (draftId: string, field: 'start' | 'end', value: string) => {
    setSlotModalError('');
    setSlotDrafts((prev) =>
      prev.map((draft) => {
        if (draft.id !== draftId) return draft;
        return { ...draft, [field]: value };
      })
    );
  };

  const removeModalDraft = (draftId: string) => {
    setSlotModalError('');
    setSlotDrafts((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((draft) => draft.id !== draftId);
    });
  };

  const toggleCopyDaySelector = () => {
    setSlotModalError('');
    if (isCopyDaySelectorOpen) {
      setCopyTargetDayIds([]);
    }
    setIsCopyDaySelectorOpen((prev) => !prev);
  };

  const toggleCopyTargetDay = (dayId: AvailabilityWeekday) => {
    setSlotModalError('');
    setCopyTargetDayIds((prev) =>
      prev.includes(dayId) ? prev.filter((selectedDayId) => selectedDayId !== dayId) : [...prev, dayId]
    );
  };

  const applyModalSlots = async () => {
    if (!slotModalDayId || slotDrafts.length === 0) {
      closeAddSlotsModal();
      return;
    }

    const nextSlots = dedupeSlotsByTime(
      slotDrafts.map((slotDraft) => ({
        startTime: normalizeTime(slotDraft.start),
        endTime: normalizeTime(slotDraft.end),
      }))
    );

    const invalidSlot = nextSlots.find((slot) => !isValidSlot(slot));
    if (invalidSlot) {
      setSlotModalError('Use HH:mm time and make sure start time is before end time.');
      return;
    }

    const copyTargetDaySet = new Set(copyTargetDayIds);
    const cloneNextSlots = () => nextSlots.map((slot) => ({ ...slot }));
    const nextSchedule = schedule.map((day) => {
      if (day.id !== slotModalDayId && !copyTargetDaySet.has(day.id)) return day;
      return {
        ...day,
        enabled: true,
        slots: cloneNextSlots(),
      };
    });

    setIsSavingSchedule(true);
    setScheduleMutationError('');
    setSlotModalError('');
    try {
      const payload: UpdateWeeklyAvailabilitySchedulePayload = {
        ...buildWeeklySchedulePayload(nextSchedule, resolveSchedulePayloadTimezone()),
        ...(copyTargetDayIds.length > 0
          ? { copyFromDay: slotModalDayId, copyToDays: copyTargetDayIds }
          : {}),
      };
      await updateWeeklyAvailabilitySchedule(payload);
      setSchedule(buildScheduleFromApiDays(payload.days));
      setScheduleError('');
      setScheduleMutationError('');
      closeAddSlotsModal();
      showSuccessToast(
        'Availability saved',
        copyTargetDayIds.length > 0
          ? `Copied timings to ${copyTargetDayIds.length} day${copyTargetDayIds.length === 1 ? '' : 's'}.`
          : 'Your weekly schedule has been updated.',
      );
      void loadWeeklySchedule();
    } catch (error) {
      const message = formatTimeValuesInMessage(extractErrorMessage(error));
      setScheduleMutationError(message);
      setSlotModalError(message || 'Failed to save availability.');
    } finally {
      setIsSavingSchedule(false);
    }
  };

  const removeRecurringBreak = async (breakId: string) => {
    try {
      await deleteRecurringBreak(breakId);
      setRecurringBreaks((prev) => prev.filter((breakItem) => breakItem.id !== breakId));
      void loadRecurringBreaks();
    } catch (error) {
      showErrorToast(error, 'Failed to delete recurring break');
    }
  };

  const openAddRecurringBreakModal = () => {
    setRecurringBreakDraft(createRecurringBreakDraft());
    setRecurringBreakSubmitError('');
    setEditingRecurringBreakId(null);
    setIsAddRecurringBreakOpen(true);
  };

  const openEditRecurringBreakModal = (breakItem: RecurringBreak) => {
    setRecurringBreakDraft({
      name: breakItem.name,
      repeatDays: (breakItem.days || []).map((dayId) => AVAILABILITY_TO_WEEKDAY[dayId]),
      start: breakItem.startTime,
      end: breakItem.endTime,
    });
    setRecurringBreakSubmitError('');
    setEditingRecurringBreakId(breakItem.id);
    setIsAddRecurringBreakOpen(true);
  };

  const closeAddRecurringBreakModal = () => {
    setIsAddRecurringBreakOpen(false);
    setRecurringBreakSubmitError('');
    setEditingRecurringBreakId(null);
  };

  const updateRecurringBreakDraft = (updater: React.SetStateAction<RecurringBreakDraft>) => {
    setRecurringBreakSubmitError('');
    setRecurringBreakDraft(updater);
  };

  const toggleRecurringBreakDay = (dayId: WeekdayKey) => {
    updateRecurringBreakDraft((prev) => {
      if (prev.repeatDays.includes(dayId)) {
        return { ...prev, repeatDays: prev.repeatDays.filter((day) => day !== dayId) };
      }

      return { ...prev, repeatDays: [...prev.repeatDays, dayId] };
    });
  };

  const addRecurringBreak = async () => {
    setRecurringBreakSubmitError('');

    const trimmedName = recurringBreakDraft.name.trim();
    if (!trimmedName) return;
    if (recurringBreakDraft.repeatDays.length === 0) return;

    const payload: CreateRecurringBreakPayload = {
      name: trimmedName,
      days: recurringBreakDraft.repeatDays.map((dayId) => WEEKDAY_TO_AVAILABILITY[dayId]),
      startTime: normalizeTime(recurringBreakDraft.start),
      endTime: normalizeTime(recurringBreakDraft.end),
    };

    if (!isValidSlot({ startTime: payload.startTime, endTime: payload.endTime })) {
      setRecurringBreakSubmitError('Use HH:mm time and make sure start time is before end time.');
      return;
    }

    try {
      if (editingRecurringBreakId) {
        const updatedBreak = await updateRecurringBreak(editingRecurringBreakId, payload);
        setRecurringBreaks((prev) =>
          prev.map((breakItem) =>
            breakItem.id === editingRecurringBreakId
              ? {
                  id: updatedBreak.id,
                  name: updatedBreak.name,
                  days: updatedBreak.days || [],
                  startTime: normalizeTime(updatedBreak.startTime),
                  endTime: normalizeTime(updatedBreak.endTime),
                }
              : breakItem
          )
        );
        closeAddRecurringBreakModal();
        void loadRecurringBreaks();
        return;
      }

      const createdBreak = await createRecurringBreak(payload);
      setRecurringBreaks((prev) => [
        ...prev,
        {
          id: createdBreak.id,
          name: createdBreak.name,
          days: createdBreak.days || [],
          startTime: normalizeTime(createdBreak.startTime),
          endTime: normalizeTime(createdBreak.endTime),
        },
      ]);

      closeAddRecurringBreakModal();
      void loadRecurringBreaks();
    } catch (error) {
      const message = formatTimeValuesInMessage(extractErrorMessage(error));
      setRecurringBreakSubmitError(message || 'Failed to save recurring break.');
    }
  };

  const openBlockDatesModal = () => {
    setBlockDatesMonth(new Date());
    setBlockStartDate(null);
    setBlockEndDate(null);
    setBlockReason('');
    setBlockDatesSubmitError('');
    setPendingTimeOffPayload(null);
    setAppointmentConflictCount(0);
    setRescheduleAppointments([]);
    setTimeOffConflictError('');
    setIsBlockDatesOpen(true);
  };

  const closeBlockDatesModal = () => {
    setBlockDatesSubmitError('');
    setIsBlockDatesOpen(false);
  };

  const applyBlockDateRange = (start: Date, end: Date | null) => {
    const normalizedStart = startOfDay(start);
    const normalizedEnd = end ? startOfDay(end) : null;
    setBlockStartDate(normalizedStart);
    setBlockEndDate(normalizedEnd);
    setBlockDatesMonth(normalizedStart);
    setBlockDatesSubmitError('');
  };

  const handleCalendarDateSelect = (selectedDate: Date) => {
    const normalizedDate = startOfDay(selectedDate);
    if (isPastScheduleDay(normalizedDate) || isBlockedDateAlreadySaved(normalizedDate)) return;

    setBlockDatesSubmitError('');

    if (!blockStartDate || (blockStartDate && blockEndDate)) {
      applyBlockDateRange(normalizedDate, null);
      return;
    }

    if (
      isAfter(normalizedDate, blockStartDate) &&
      rangeIntersectsBlockedDateRanges(blockStartDate, normalizedDate, timeOffEntries)
    ) {
      setBlockDatesSubmitError(BLOCK_DATES_ALREADY_BLOCKED_ERROR);
      return;
    }

    applyBlockDateRange(blockStartDate, normalizedDate);
  };

  const handleBlockStartDatePickerChange = (value: dayjs.Dayjs | null) => {
    if (!value?.isValid()) {
      setBlockStartDate(null);
      setBlockEndDate(null);
      setBlockDatesSubmitError('');
      return;
    }

    const nextStartDate = startOfDay(value.toDate());
    if (isBlockedDateAlreadySaved(nextStartDate)) {
      setBlockDatesSubmitError(BLOCK_DATES_ALREADY_BLOCKED_ERROR);
      return;
    }

    applyBlockDateRange(nextStartDate, blockEndDate);
  };

  const handleBlockEndDatePickerChange = (value: dayjs.Dayjs | null) => {
    if (!value?.isValid()) {
      setBlockEndDate(null);
      setBlockDatesSubmitError('');
      return;
    }

    if (!blockStartDate) {
      applyBlockDateRange(value.toDate(), null);
      return;
    }

    const nextEndDate = startOfDay(value.toDate());
    if (
      isBlockedDateAlreadySaved(nextEndDate) ||
      (!isBefore(nextEndDate, blockStartDate) &&
        rangeIntersectsBlockedDateRanges(blockStartDate, nextEndDate, timeOffEntries))
    ) {
      setBlockDatesSubmitError(BLOCK_DATES_ALREADY_BLOCKED_ERROR);
      return;
    }

    applyBlockDateRange(blockStartDate, nextEndDate);
  };

  const blockDatesMinDay = dayjs().startOf('day');
  const blockStartMinDay = blockStartDate ? dayjs(blockStartDate).startOf('day') : null;
  const blockEndDateMinDay =
    blockStartMinDay && blockStartMinDay.isAfter(blockDatesMinDay) ? blockStartMinDay : blockDatesMinDay;

  const finalizeTimeOffCreation = async (payload: CreateTimeOffPayload) => {
    const createdTimeOff = await createTimeOff(payload);
    const createdEntry = mapApiTimeOffEntry(createdTimeOff, timeOffEntries.length);

    if (createdEntry) {
      setTimeOffEntries((prev) => [...prev.filter((entry) => entry.id !== createdEntry.id), createdEntry]);
    }

    setPendingTimeOffPayload(null);
    setAppointmentConflictCount(0);
    setRescheduleAppointments([]);
  };

  const finishPendingTimeOffIfResolved = async (remainingAppointments: RescheduleAppointment[]) => {
    if (remainingAppointments.length > 0 || !pendingTimeOffPayload) return;

    setIsSavingTimeOff(true);
    try {
      await finalizeTimeOffCreation(pendingTimeOffPayload);
      setIsAppointmentConflictOpen(false);
      setIsRescheduleSessionOpen(false);
      setTimeOffConflictError('');
    } catch (error) {
      setTimeOffConflictError(formatTimeValuesInMessage(extractErrorMessage(error)) || 'Failed to add time off.');
    } finally {
      setIsSavingTimeOff(false);
    }
  };

  const markAppointmentConflictResolved = async (appointmentId: string) => {
    const remainingAppointments = rescheduleAppointments.filter((appointment) => appointment.id !== appointmentId);
    setRescheduleAppointments(remainingAppointments);
    setAppointmentConflictCount(remainingAppointments.length);

    if (remainingAppointments.length === 0) {
      await finishPendingTimeOffIfResolved(remainingAppointments);
      if (!pendingTimeOffPayload) {
        setIsRescheduleSessionOpen(false);
        setIsAppointmentConflictOpen(false);
      }
    }
  };

  const addTimeOffEntry = async () => {
    if (!blockStartDate) {
      setBlockDatesSubmitError(BLOCK_DATES_SELECT_ERROR);
      return;
    }

    const validationError = getBlockDatesValidationError(blockStartDate, blockEndDate, timeOffEntries);
    if (validationError) {
      setBlockDatesSubmitError('');
      return;
    }

    setBlockDatesSubmitError('');
    const effectiveEndDate = blockEndDate || blockStartDate;
    const payload: CreateTimeOffPayload = {
      startDate: toApiDateOnly(blockStartDate),
      endDate: toApiDateOnly(effectiveEndDate),
      reason: blockReason.trim() || 'Blocked Date',
    };

    setIsSavingTimeOff(true);
    try {
      const checkedTimeOff = await createTimeOff(payload);
      const conflictCount = getTimeOffConflictCount(checkedTimeOff);

      if (conflictCount > 0) {
        const checkedTimeOffId = getTimeOffId(checkedTimeOff);
        if (checkedTimeOffId) {
          await deleteTimeOff(checkedTimeOffId);
        }

        const pendingEntry = mapApiTimeOffEntry(checkedTimeOff, 0);
        const appointmentsToReschedule = buildRescheduleAppointments(
          checkedTimeOff,
          conflictCount,
          pendingEntry,
        );

        setPendingTimeOffPayload(payload);
        setAppointmentConflictCount(conflictCount);
        setRescheduleAppointments(appointmentsToReschedule);
        setTimeOffConflictError('');
        setBlockReason('');
        closeBlockDatesModal();
        setIsAppointmentConflictOpen(true);
        return;
      }

      const createdEntry = mapApiTimeOffEntry(checkedTimeOff, timeOffEntries.length);
      if (createdEntry) {
        setTimeOffEntries((prev) => [...prev.filter((entry) => entry.id !== createdEntry.id), createdEntry]);
      }

      setPendingTimeOffPayload(null);
      setAppointmentConflictCount(0);
      setRescheduleAppointments([]);
      setBlockReason('');
      closeBlockDatesModal();
    } catch (error) {
      setBlockDatesSubmitError(formatTimeValuesInMessage(extractErrorMessage(error)) || 'Failed to add time off.');
    } finally {
      setIsSavingTimeOff(false);
    }
  };

  const removeTimeOffEntry = async (timeOffId: string) => {
    setIsSavingTimeOff(true);
    try {
      await deleteTimeOff(timeOffId);
      setTimeOffEntries((prev) => prev.filter((entry) => entry.id !== timeOffId));
      void loadTimeOffEntries();
    } catch (error) {
      showErrorToast(error, 'Failed to delete time off');
    } finally {
      setIsSavingTimeOff(false);
    }
  };

  const closeAppointmentConflictModal = () => {
    setTimeOffConflictError('');
    setIsAppointmentConflictOpen(false);
  };

  const resolveAppointmentConflictByCancelling = () => {
    setPendingTimeOffPayload(null);
    setRescheduleAppointments([]);
    setAppointmentConflictCount(0);
    setTimeOffConflictError('');
    setIsAppointmentConflictOpen(false);
    setIsRescheduleSessionOpen(false);
  };

  const resolveAppointmentConflictByRescheduling = () => {
    setTimeOffConflictError('');
    setIsAppointmentConflictOpen(false);
    setIsRescheduleSessionOpen(true);
  };

  const closeRescheduleSessionModal = () => {
    setTimeOffConflictError('');
    setIsRescheduleSessionOpen(false);
  };

  const cancelRescheduleAppointment = async (appointmentId: string) => {
    setCancellingRescheduleId(appointmentId);
    setTimeOffConflictError('');
    try {
      await cancelAppointment(appointmentId, 'this_only');
      await markAppointmentConflictResolved(appointmentId);
    } catch (error) {
      setTimeOffConflictError(extractErrorMessage(error) || 'Failed to cancel appointment.');
    } finally {
      setCancellingRescheduleId('');
    }
  };

  const openAppointmentRescheduleDialog = (appointment: RescheduleAppointment) => {
    setRescheduleAppointmentId(appointment.id);
    setIsScheduleAppointmentOpen(true);
  };

  const closeAppointmentRescheduleDialog = () => {
    setIsScheduleAppointmentOpen(false);
    setRescheduleAppointmentId(null);
  };

  const handleAppointmentRescheduled = async () => {
    const updatedAppointmentId = rescheduleAppointmentId ? String(rescheduleAppointmentId) : '';
    if (updatedAppointmentId) {
      await markAppointmentConflictResolved(updatedAppointmentId);
    }
  };

  return (
    <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '28px',
          border: '1px solid #dbe6eb',
          bgcolor: '#FCFEFF',
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: 2.5 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: '14px',
              border: '1px solid #dbe6eb',
              bgcolor: '#FFFFFF',
              px: { xs: 2, sm: 3 },
              py: 1.25,
            }}
          >
            <Breadcrumbs separator="/" aria-label="breadcrumb">
              <Link component={RouterLink} to="/profile?view=hub" underline="hover" sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#64748b' }}>
                Settings
              </Link>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#0f172a' }}>
                Availability
              </Typography>
            </Breadcrumbs>
          </Paper>
        </Box>

        <Box sx={{ px: { xs: 2, sm: 3, lg: 3.5 }, pb: { xs: 2.5, sm: 3.5 }, pt: { xs: 2, sm: 2.5 } }}>
          <Box sx={{ mb: 2.15, display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
            <IconButton
              aria-label="Back to settings"
              onClick={() => navigate('/profile?view=hub')}
              sx={{
                mt: 0.3,
                width: 30,
                height: 30,
                color: '#1F2B34',
                p: 0,
                '&:hover': {
                  bgcolor: 'transparent',
                },
              }}
            >
              <ArrowLeft size={28} strokeWidth={2.1} />
            </IconButton>

            <Box sx={{ minWidth: 0, pt: 0.05 }}>
              <Typography sx={{ color: '#1F2B34', fontWeight: 700, fontSize: { xs: '1.28rem', sm: '1.41rem' }, lineHeight: 1.05 }}>
                Availability
              </Typography>
              <Typography sx={{ mt: 0.65, color: '#676B80', fontSize: { xs: '0.8rem', sm: '0.92rem' }, lineHeight: 1.2 }}>
                Working hours, breaks, and blocked dates
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, sm: 3.5 }, borderBottom: '1px solid #e6edf2' }}>
            {AVAILABILITY_TABS.map((tab) => {
              const isActive = tab.id === activeSection;
              return (
                <Button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveSection(tab.id)}
                  sx={{
                    px: 0,
                    py: 1.2,
                    minWidth: 0,
                    borderRadius: 0,
                    textTransform: 'none',
                    fontWeight: isActive ? 700 : 500,
                    fontSize: { xs: '0.75rem', sm: '0.82rem' },
                    color: isActive ? '#25A7D8' : '#6A7785',
                    borderBottom: isActive ? '2px solid #25A7D8' : '2px solid transparent',
                  }}
                >
                  {tab.label}
                </Button>
              );
            })}
          </Box>

          {activeSection === 'weekly' ? (
            <Box sx={{ mt: 2 }}>
              <Box
                sx={{
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  border: '1px solid #E8C88E',
                  borderRadius: '10px',
                  bgcolor: 'transparent',
                  px: { xs: 1.3, sm: 1.6 },
                  py: 1.1,
                }}
              >
                <Box
                  component="img"
                  src={appointmentRequestReschedulePendingIcon}
                  alt=""
                  sx={{ width: 16, height: 18, display: 'block' }}
                />
                <Box>
                  <Typography sx={{ color: '#2E343C', fontSize: '0.75rem', fontWeight: 700 }}>Your schedule is not public</Typography>
                  <Typography sx={{ color: '#55626F', fontSize: '0.83rem', mt: 0.35 }}>
                    Clients can only book during your available hours. Click + Slot to add multiple windows per day.
                  </Typography>
                </Box>
              </Box>

              {scheduleMutationError ? (
                <Box
                  sx={{
                    mb: 2,
                    border: '1px solid #F0B5B5',
                    borderRadius: '10px',
                    bgcolor: '#FFF7F7',
                    px: { xs: 1.3, sm: 1.6 },
                    py: 1.2,
                  }}
                >
                  <Typography sx={{ color: '#8A2D2D', fontSize: '0.78rem', fontWeight: 700 }}>
                    Could not save weekly schedule
                  </Typography>
                  <Typography sx={{ color: '#915252', fontSize: '0.83rem', mt: 0.35 }}>{scheduleMutationError}</Typography>
                </Box>
              ) : null}

              {scheduleError ? (
                <Box
                  sx={{
                    mb: 2,
                    border: '1px solid #F0B5B5',
                    borderRadius: '10px',
                    bgcolor: '#FFF7F7',
                    px: { xs: 1.3, sm: 1.6 },
                    py: 1.2,
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: 1.5,
                    flexDirection: { xs: 'column', sm: 'row' },
                  }}
                >
                  <Box>
                    <Typography sx={{ color: '#8A2D2D', fontSize: '0.78rem', fontWeight: 700 }}>
                      Could not load weekly schedule
                    </Typography>
                    <Typography sx={{ color: '#915252', fontSize: '0.83rem', mt: 0.35 }}>{scheduleError}</Typography>
                  </Box>
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={() => void loadWeeklySchedule()}
                    disabled={isLoadingSchedule}
                    sx={{
                      height: 30,
                      minWidth: 74,
                      borderRadius: '10px',
                      textTransform: 'none',
                      fontWeight: 700,
                      color: '#8A2D2D',
                      borderColor: '#E5A0A0',
                      '&:hover': {
                        borderColor: '#D08383',
                        bgcolor: '#FFF1F1',
                      },
                    }}
                  >
                    Retry
                  </Button>
                </Box>
              ) : null}

              {isLoadingSchedule ? (
                <Box
                  sx={{
                    minHeight: 240,
                    borderRadius: '14px',
                    border: '1px solid #E2EAF0',
                    bgcolor: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.2,
                    color: '#5E6C78',
                    fontSize: '0.92rem',
                  }}
                >
                  <CircularProgress size={22} thickness={5} sx={{ color: '#25A7D8' }} />
                  Loading availability...
                </Box>
              ) : (
                <Box sx={{ overflow: 'hidden', borderRadius: '14px', border: '1px solid #E2EAF0', bgcolor: '#fff' }}>
                <Box sx={{ px: { xs: 1.5, sm: 2.2 }, py: 1.4, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarDays size={17} color="#32A7D8" />
                  <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#1F2B34' }}>Set your Availability</Typography>
                </Box>

                <Box
                  sx={{
                    pl: { xs: 1.5, sm: 2.5, md: '42px' },
                    pr: { xs: 1.5, sm: 2.5 },
                    py: 2.33,
                    minHeight: { md: 66 },
                    bgcolor: '#F3FAFF',
                    borderTop: '1px solid #E2EAF0',
                    borderBottom: '1px solid #E2EAF0',
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '120px 126px minmax(0,1fr)' },
                    columnGap: 1,
                    rowGap: 0.75,
                  }}
                >
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#2A3640' }}>Day</Typography>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#2A3640', textAlign: 'center' }}>On</Typography>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#2A3640', pl: { md: 3 } }}>Hours</Typography>
                </Box>

                {schedule.map((day) => (
                  <Box
                    key={day.id}
                    sx={{
                      pl: { xs: 1.5, sm: 2.5, md: '42px' },
                      pr: { xs: 1.5, sm: 2.5 },
                      py: 2.5,
                      borderBottom: '1px solid #EEF3F7',
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '120px 126px minmax(0,1fr)' },
                      columnGap: 1,
                      rowGap: { xs: 1.2, md: 0.7 },
                      alignItems: 'center',
                    }}
                  >
                    <Typography sx={{ fontSize: '0.97rem', fontWeight: 500, color: '#222E37' }}>{day.label}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Switch
                        checked={day.enabled}
                        disabled={isWeeklySwitchBusy}
                        onChange={(_, checked) => toggleDay(day.id, checked)}
                        sx={{
                          '& .MuiSwitch-track': {
                            borderRadius: 50,
                            bgcolor: '#D6DDE4',
                            opacity: 1,
                          },
                          '& .MuiSwitch-thumb': {
                            boxShadow: 'none',
                            border: '2px solid #C9D3DD',
                            backgroundColor: '#fff',
                          },
                          '& .MuiSwitch-switchBase': {
                            color: '#fff',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': {
                            borderColor: '#18B7A7',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            bgcolor: '#18B7A7',
                            opacity: 1,
                          },
                        }}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 4, rowGap: 1, pl: { md: 3 } }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 4, rowGap: 1 }}>
                        {day.slots.length > 0 ? (
                          day.slots.map((slot, slotIndex) => {
                            const slotLabel = formatSlotLabel(slot);
                            return (
                            <Box
                              key={`${day.id}-${slot.startTime}-${slot.endTime}-${slotIndex}`}
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1.1,
                                px: 1.3,
                                py: 0.55,
                                border: '1px solid #D7E1EA',
                                borderRadius: '9px',
                                bgcolor: '#fff',
                              }}
                            >
                              <Typography sx={{ color: '#1F2B34', fontSize: '0.87rem', lineHeight: 1 }}>{slotLabel}</Typography>
                              <IconButton
                                aria-label={`Remove ${slotLabel} from ${day.label}`}
                                size="small"
                                disabled={isWeeklyScheduleBusy}
                                onClick={() => removeExistingSlot(day.id, slotIndex)}
                                sx={{
                                  p: 0,
                                  color: '#64717D',
                                  '&:hover': { bgcolor: 'transparent', color: '#33414C' },
                                }}
                              >
                                <X size={14} />
                              </IconButton>
                            </Box>
                            );
                          })
                        ) : (
                          <Typography sx={{ color: '#7A8792', fontSize: '0.85rem' }}>No slots added</Typography>
                        )}
                      </Box>

                      <Button
                        type="button"
                        variant="outlined"
                        onClick={() => openAddSlotsModal(day.id)}
                        disabled={isWeeklyScheduleBusy}
                        sx={{
                          minWidth: 86,
                          height: 30,
                          borderRadius: '12px',
                          px: 1.1,
                          textTransform: 'none',
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          borderColor: '#D2DCE6',
                          color: '#556472',
                          '&:hover': {
                            borderColor: '#B8C8D6',
                            bgcolor: '#F7FBFF',
                          },
                        }}
                      >
                        Add Slot +
                      </Button>
                    </Box>
                  </Box>
                ))}
                </Box>
              )}
            </Box>
          ) : activeSection === 'breaks' ? (
            <Box
              sx={{
                mt: 2.3,
                maxWidth: { lg: 'calc(50% - 8px)' },
              }}
            >
              <Box
                sx={{
                  border: '1px solid #E2EAF0',
                  borderRadius: '18px',
                  bgcolor: '#fff',
                  px: { xs: 1.8, sm: 2.3 },
                  pt: 2.1,
                  pb: 1.3,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.2 }}>
                  <Box>
                    <Typography sx={{ color: '#1F2B34', fontSize: '1.38rem', fontWeight: 700, lineHeight: 1 }}>Recurring Breaks</Typography>
                    <Typography sx={{ mt: 0.9, color: '#6D7A86', fontSize: '0.92rem' }}>Regular blocks across applicable days</Typography>
                  </Box>

                  <Button
                    type="button"
                    variant="outlined"
                    onClick={openAddRecurringBreakModal}
                    sx={{
                      mt: 0.1,
                      minWidth: 108,
                      height: 32,
                      borderRadius: '10px',
                      px: 1.25,
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      borderColor: '#D2DCE6',
                      color: '#556472',
                      '&:hover': {
                        borderColor: '#B8C8D6',
                        bgcolor: '#F7FBFF',
                      },
                    }}
                  >
                    Add Break +
                  </Button>
                </Box>

                <Box sx={{ mt: 1.8 }}>
                  {recurringBreaks.length > 0 ? (
                    recurringBreaks.map((breakItem, breakIndex) => (
                      <Box
                        key={breakItem.id}
                        sx={{
                          py: 1.45,
                          borderBottom: breakIndex === recurringBreaks.length - 1 ? 'none' : '1px solid #E6EDF3',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 1.5,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
                          <Box
                            sx={{
                              width: 18,
                              height: 18,
                              borderRadius: '50%',
                              bgcolor: '#9ADDEE',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Clock3 size={10} color="#249FCB" />
                          </Box>
                          <Box>
                            <Typography sx={{ color: '#1F2B34', fontSize: '0.82rem', fontWeight: 700 }}>{breakItem.name}</Typography>
                            <Typography sx={{ mt: 0.4, color: '#7A8894', fontSize: '0.88rem' }}>
                              {formatRecurringBreakSubtitle(breakItem.days, breakItem.startTime, breakItem.endTime)}
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                          <Button
                            type="button"
                            variant="outlined"
                            onClick={() => openEditRecurringBreakModal(breakItem)}
                            sx={{
                              height: 37,
                              minWidth: 60,
                              borderRadius: '12px',
                              textTransform: 'none',
                              fontWeight: 700,
                              fontSize: '0.95rem',
                              color: '#435261',
                              borderColor: '#D7E1EA',
                              '&:hover': {
                                borderColor: '#C1CFDB',
                                bgcolor: '#F8FBFE',
                              },
                            }}
                          >
                            Edit
                          </Button>

                          <IconButton
                            aria-label={`Delete ${breakItem.name}`}
                            onClick={() => void removeRecurringBreak(breakItem.id)}
                            sx={{
                              color: '#FA8D8D',
                              width: 28,
                              height: 28,
                              '&:hover': {
                                bgcolor: '#FFF3F3',
                                color: '#F56D6D',
                              },
                            }}
                          >
                            <Trash2 size={17} />
                          </IconButton>
                        </Box>
                      </Box>
                    ))
                  ) : (
                    <Typography sx={{ color: '#7A8894', fontSize: '0.92rem', py: 1 }}>No recurring breaks configured</Typography>
                  )}
                </Box>
              </Box>
            </Box>
          ) : activeSection === 'timeoff' ? (
            <Box sx={{ mt: 2.3 }}>
              <Box
                sx={{
                  border: '1px solid #E2EAF0',
                  borderRadius: '18px',
                  bgcolor: '#fff',
                  px: { xs: 1.8, sm: 2.3 },
                  pt: 2.1,
                  pb: 1.3,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.2 }}>
                  <Box>
                    <Typography sx={{ color: '#1F2B34', fontSize: '1.38rem', fontWeight: 700, lineHeight: 1 }}>
                      Time Off and Blocked Dates
                    </Typography>
                    <Typography sx={{ mt: 0.9, color: '#6D7A86', fontSize: '0.92rem' }}>
                      Clients cannot book during these periods
                    </Typography>
                  </Box>

                  <Button
                    type="button"
                    variant="contained"
                    disableElevation
                    onClick={openBlockDatesModal}
                    disabled={isTimeOffBusy}
                    sx={{
                      mt: 0.1,
                      minWidth: 108,
                      height: 34,
                      borderRadius: '12px',
                      px: 1.4,
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      bgcolor: '#25A7D8',
                      '&:hover': {
                        bgcolor: '#1795c7',
                      },
                      '&.Mui-disabled': {
                        bgcolor: '#9FD7EA',
                        color: 'rgba(255,255,255,0.9)',
                      },
                    }}
                  >
                    Block Dates +
                  </Button>
                </Box>

                <Box sx={{ mt: 1.8 }}>
                  {timeOffError ? (
                    <Box
                      sx={{
                        mb: 1.6,
                        border: '1px solid #F0B5B5',
                        borderRadius: '10px',
                        bgcolor: '#FFF7F7',
                        px: { xs: 1.3, sm: 1.6 },
                        py: 1.2,
                        display: 'flex',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: 1.5,
                        flexDirection: { xs: 'column', sm: 'row' },
                      }}
                    >
                      <Box>
                        <Typography sx={{ color: '#8A2D2D', fontSize: '0.78rem', fontWeight: 700 }}>
                          Could not load time off
                        </Typography>
                        <Typography sx={{ color: '#915252', fontSize: '0.83rem', mt: 0.35 }}>{timeOffError}</Typography>
                      </Box>
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={() => void loadTimeOffEntries()}
                        disabled={isLoadingTimeOff}
                        sx={{
                          height: 30,
                          minWidth: 74,
                          borderRadius: '10px',
                          textTransform: 'none',
                          fontWeight: 700,
                          color: '#8A2D2D',
                          borderColor: '#E5A0A0',
                          '&:hover': {
                            borderColor: '#D08383',
                            bgcolor: '#FFF1F1',
                          },
                        }}
                      >
                        Retry
                      </Button>
                    </Box>
                  ) : null}

                  {isLoadingTimeOff ? (
                    <Box
                      sx={{
                        minHeight: 160,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1.2,
                        color: '#5E6C78',
                        fontSize: '0.92rem',
                      }}
                    >
                      <CircularProgress size={22} thickness={5} sx={{ color: '#25A7D8' }} />
                      Loading time off...
                    </Box>
                  ) : timeOffEntries.length > 0 ? (
                    timeOffEntries.map((entry, index) => (
                      <Box
                        key={entry.id}
                        sx={{
                          py: 1.45,
                          borderBottom: index === timeOffEntries.length - 1 ? 'none' : '1px solid #E6EDF3',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 1.5,
                        }}
                      >
                        <Box>
                          <Typography sx={{ color: '#1F2B34', fontSize: '0.82rem', fontWeight: 700 }}>{entry.dateRange}</Typography>
                          <Typography sx={{ mt: 0.4, color: '#7A8894', fontSize: '0.88rem' }}>{entry.note}</Typography>
                        </Box>

                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              minWidth: 68,
                              height: 26,
                              borderRadius: '10px',
                              px: 1.1,
                              bgcolor: '#F3E5D5',
                              color: '#3D352E',
                              fontSize: '0.86rem',
                              fontWeight: 500,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {entry.daysLabel}
                          </Box>
                          <IconButton
                            aria-label={`Delete time off ${entry.dateRange}`}
                            onClick={() => void removeTimeOffEntry(entry.id)}
                            disabled={isTimeOffBusy}
                            sx={{
                              color: '#FA8D8D',
                              width: 40,
                              height: 40,
                              p: 0,
                              '&:hover': {
                                bgcolor: '#FFF3F3',
                                color: '#F56D6D',
                              },
                            }}
                          >
                            <Box
                              component="img"
                              src={trashBinTrashIcon}
                              alt=""
                              sx={{ width: 24, height: 24, opacity: 0.95 }}
                            />
                          </IconButton>
                        </Box>
                      </Box>
                    ))
                  ) : (
                    <Typography sx={{ color: '#7A8894', fontSize: '0.92rem', py: 1 }}>No blocked dates added</Typography>
                  )}
                </Box>
              </Box>
            </Box>
          ) : null}
        </Box>
      </Paper>

      <Dialog
        open={Boolean(activeDay)}
        onClose={closeAddSlotsModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: { xs: 'calc(100dvh - 24px)', sm: 'calc(100dvh - 40px)' },
            overflow: 'hidden',
            p: 0,
            maxWidth: '560px',
          },
        }}
      >
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            pt: 2.5,
            pb: 2,
            minHeight: 0,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
            <Box>
              <Typography sx={{ color: '#1E2A36', fontWeight: 700, fontSize: { xs: '1.16rem', sm: '1.6rem' }, lineHeight: 1 }}>
                Add Slots - {activeDay?.label}
              </Typography>
              <Typography sx={{ mt: 1, color: '#687886', fontSize: '0.93rem' }}>
                Set availability windows for {activeDay?.label}
              </Typography>
            </Box>

            <IconButton
              aria-label="Close add slots modal"
              onClick={closeAddSlotsModal}
              sx={{
                mt: 0.4,
                bgcolor: '#EDF1F5',
                color: '#73818D',
                width: 32,
                height: 32,
                '&:hover': {
                  bgcolor: '#E2E8EF',
                },
              }}
            >
              <X size={18} />
            </IconButton>
          </Box>

          <Box
            sx={{
              mt: 2.2,
              borderTop: '1px solid #EDF2F6',
              maxHeight: { xs: '42dvh', sm: '48dvh' },
              overflowY: 'auto',
              pr: 0.6,
              mr: -0.6,
              scrollbarGutter: 'stable',
            }}
          >
            {slotDrafts.map((slotDraft, index) => (
              <Box
                key={slotDraft.id}
                sx={{
                  py: 1.8,
                  borderBottom: '1px solid #EDF2F6',
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'minmax(0,155px) 24px minmax(0,155px) 24px' },
                  alignItems: 'center',
                  columnGap: 1.2,
                  rowGap: 1,
                }}
              >
                <TimeSelect
                  value={slotDraft.start}
                  disabled={isWeeklyScheduleBusy}
                  required
                  onChange={(nextValue) => updateModalDraft(slotDraft.id, 'start', nextValue)}
                />
                <Typography sx={{ color: '#667380', fontSize: '1rem', textAlign: 'center', display: { xs: 'none', sm: 'block' } }}>
                  -
                </Typography>
                <TimeSelect
                  value={slotDraft.end}
                  disabled={isWeeklyScheduleBusy}
                  required
                  onChange={(nextValue) => updateModalDraft(slotDraft.id, 'end', nextValue)}
                />
                <IconButton
                  aria-label={`Remove draft slot ${index + 1}`}
                  onClick={() => removeModalDraft(slotDraft.id)}
                  sx={{
                    color: '#7A8894',
                    width: 24,
                    height: 24,
                    '&:hover': {
                      bgcolor: 'transparent',
                      color: '#4F5B66',
                    },
                  }}
                >
                  <X size={17} />
                </IconButton>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              mt: 1.35,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1.2,
            }}
          >
            <Button
              type="button"
              onClick={addModalDraft}
              disabled={isWeeklyScheduleBusy}
              sx={{
                px: 0,
                minWidth: 0,
                textTransform: 'none',
                fontSize: '1.05rem',
                fontWeight: 700,
                color: '#24A7D8',
                '&:hover': {
                  bgcolor: 'transparent',
                  color: '#128FC0',
                },
              }}
            >
              Add More +
            </Button>

            <Button
              type="button"
              variant="outlined"
              startIcon={<Copy size={15} />}
              onClick={toggleCopyDaySelector}
              disabled={isWeeklyScheduleBusy}
              sx={{
                minHeight: 34,
                borderRadius: '11px',
                px: 1.35,
                textTransform: 'none',
                fontSize: '0.84rem',
                fontWeight: 700,
                borderColor: copyTargetDayIds.length > 0 ? '#25A7D8' : '#D2DCE6',
                color: copyTargetDayIds.length > 0 ? '#168DB9' : '#556472',
                bgcolor: copyTargetDayIds.length > 0 ? '#F1FBFE' : '#fff',
                '&:hover': {
                  borderColor: '#25A7D8',
                  bgcolor: '#F7FBFF',
                },
                '& .MuiButton-startIcon': {
                  mr: 0.65,
                },
              }}
            >
              {copyTargetDayIds.length > 0
                ? `Copy to ${copyTargetDayIds.length} day${copyTargetDayIds.length === 1 ? '' : 's'}`
                : 'Copy to days'}
            </Button>
          </Box>

          {isCopyDaySelectorOpen ? (
            <Box
              sx={{
                mt: 1.5,
                p: 1.4,
                borderRadius: '12px',
                border: '1px solid #DCE8F0',
                bgcolor: '#F8FBFD',
              }}
            >
              <Typography sx={{ color: '#4E5F6D', fontSize: '0.82rem', fontWeight: 700 }}>
                Copy to
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                {schedule
                  .filter((day) => day.id !== activeDay?.id)
                  .map((day) => {
                    const isSelected = copyTargetDayIds.includes(day.id);

                    return (
                      <Button
                        key={`copy-target-${day.id}`}
                        type="button"
                        variant={isSelected ? 'contained' : 'outlined'}
                        disableElevation
                        aria-pressed={isSelected}
                        onClick={() => toggleCopyTargetDay(day.id)}
                        disabled={isWeeklyScheduleBusy}
                        sx={{
                          minWidth: 0,
                          height: 32,
                          px: 1.2,
                          borderRadius: '10px',
                          textTransform: 'none',
                          fontSize: '0.8rem',
                          fontWeight: 700,
                          borderColor: isSelected ? '#25A7D8' : '#D2DCE6',
                          color: isSelected ? '#fff' : '#52616F',
                          bgcolor: isSelected ? '#25A7D8' : '#fff',
                          '&:hover': {
                            borderColor: '#25A7D8',
                            bgcolor: isSelected ? '#1795c7' : '#F1FBFE',
                          },
                        }}
                      >
                        {day.label}
                      </Button>
                    );
                  })}
              </Box>
            </Box>
          ) : null}

          {slotModalError ? (
            <Box
              role="alert"
              sx={{
                mt: 1.4,
                px: 1.5,
                py: 1,
                borderRadius: '10px',
                border: '1px solid #F4B4B4',
                bgcolor: '#FFF5F5',
              }}
            >
              <Typography sx={{ color: '#C62828', fontSize: '0.86rem', fontWeight: 600 }}>
                {slotModalError}
              </Typography>
            </Box>
          ) : null}

          <Box sx={{ mt: 2.7, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="button"
              variant="contained"
              disableElevation
              onClick={() => void applyModalSlots()}
              disabled={isWeeklyScheduleBusy}
              sx={{
                minWidth: 132,
                height: 44,
                borderRadius: '14px',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '1.1rem',
                bgcolor: '#25A7D8',
                '&:hover': {
                  bgcolor: '#1795c7',
                },
              }}
            >
              Done
            </Button>
          </Box>
        </Box>
      </Dialog>

      <Dialog
        open={isAddRecurringBreakOpen}
        onClose={closeAddRecurringBreakModal}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
            p: 0,
            maxWidth: '760px',
          },
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2.5, pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
            <Box>
              <Typography sx={{ color: '#1E2A36', fontWeight: 700, fontSize: { xs: '1.16rem', sm: '1.6rem' }, lineHeight: 1 }}>
                {editingRecurringBreakId ? 'Edit Break' : 'Add Break'}
              </Typography>
              <Typography sx={{ mt: 1, color: '#687886', fontSize: '0.93rem' }}>
                This block repeats every week on the days you choose
              </Typography>
            </Box>

            <IconButton
              aria-label="Close add recurring break modal"
              onClick={closeAddRecurringBreakModal}
              sx={{
                mt: 0.4,
                bgcolor: '#EDF1F5',
                color: '#73818D',
                width: 32,
                height: 32,
                '&:hover': {
                  bgcolor: '#E2E8EF',
                },
              }}
            >
              <X size={18} />
            </IconButton>
          </Box>

          <Box sx={{ mt: 1.9, pb: 2.1 }}>
            <AppOutlinedTextField
              fullWidth
              required
              disabled={isTimeOffBusy}
              sx={{
                '& .MuiOutlinedInput-root': {
                  minHeight: 44,
                  borderRadius: '6px',
                },
                '& .MuiOutlinedInput-input': {
                  px: 1.5,
                  py: 1.2,
                  fontSize: '0.96rem',
                },
              }}
              label="Break name"
              placeholder="Eg. Lunch break, Time out"
              value={recurringBreakDraft.name}
              onChange={(event) => updateRecurringBreakDraft((prev) => ({ ...prev, name: event.target.value }))}
            />

            <Typography sx={{ color: '#1E2A36', fontSize: '0.77rem', fontWeight: 700, mt: 1.75, mb: 0.8 }}>Repeats *</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.85 }}>
              {WEEKDAY_OPTIONS.map((dayOption) => {
                const isSelected = recurringBreakDraft.repeatDays.includes(dayOption.id);
                return (
                  <Button
                    key={dayOption.id}
                    type="button"
                    onClick={() => toggleRecurringBreakDay(dayOption.id)}
                    sx={{
                      minWidth: 48,
                      height: 24,
                      borderRadius: '999px',
                      px: 1.1,
                      border: '1px solid',
                      borderColor: isSelected ? '#25A7D8' : '#D4DEE7',
                      bgcolor: isSelected ? '#25A7D8' : '#F7FAFC',
                      color: isSelected ? '#fff' : '#6A7683',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      '&:hover': {
                        bgcolor: isSelected ? '#1A96C4' : '#EDF3F8',
                        borderColor: isSelected ? '#1A96C4' : '#C7D4E0',
                      },
                    }}
                  >
                    {dayOption.label}
                  </Button>
                );
              })}
            </Box>

            <Typography sx={{ color: '#1E2A36', fontSize: '0.77rem', fontWeight: 700, mt: 1.75, mb: 0.8 }}>Break time *</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0,155px) 24px minmax(0,155px)' }, alignItems: 'center', columnGap: 1.2, rowGap: 1 }}>
              <TimeSelect
                value={recurringBreakDraft.start}
                disabled={isTimeOffBusy}
                required
                onChange={(nextValue) => updateRecurringBreakDraft((prev) => ({ ...prev, start: nextValue }))}
              />
              <Typography sx={{ color: '#667380', fontSize: '1rem', textAlign: 'center', display: { xs: 'none', sm: 'block' } }}>-</Typography>
              <TimeSelect
                value={recurringBreakDraft.end}
                disabled={isTimeOffBusy}
                required
                onChange={(nextValue) => updateRecurringBreakDraft((prev) => ({ ...prev, end: nextValue }))}
              />
            </Box>

            <Box
              sx={{
                mt: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1.1,
                border: '1px solid #8ED6F1',
                borderRadius: '8px',
                bgcolor: '#F5FCFF',
                py: 1.1,
                px: 1.35,
                maxWidth: 286,
              }}
            >
              <Lightbulb size={16} color="#25A7D8" />
              <Typography sx={{ color: '#394753', fontSize: '0.83rem' }}>Choose days and times above</Typography>
            </Box>

            {recurringBreakSubmitError ? (
              <Box
                role="alert"
                sx={{
                  mt: 1.5,
                  border: '1px solid #F0B5B5',
                  borderRadius: '10px',
                  bgcolor: '#FFF7F7',
                  px: 1.4,
                  py: 1,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                }}
              >
                <AlertCircle size={16} color="#C45C5C" style={{ marginTop: 2, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ color: '#7F1D1D', fontSize: '0.83rem', fontWeight: 700, lineHeight: 1.35 }}>
                    {editingRecurringBreakId ? 'Failed to update break' : 'Failed to add break'}
                  </Typography>
                  <Typography sx={{ mt: 0.25, color: '#915252', fontSize: '0.83rem', lineHeight: 1.4 }}>
                    {recurringBreakSubmitError}
                  </Typography>
                </Box>
              </Box>
            ) : null}
          </Box>
        </Box>

        <Box
          sx={{
            borderTop: '1px solid #E4ECF2',
            bgcolor: '#F9FBFD',
            px: { xs: 2, sm: 3 },
            py: 1.8,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            type="button"
            variant="contained"
            disableElevation
            onClick={() => void addRecurringBreak()}
            disabled={recurringBreakDraft.repeatDays.length === 0 || !recurringBreakDraft.name.trim()}
            sx={{
              minWidth: 88,
              height: 44,
              borderRadius: '14px',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '1.1rem',
              bgcolor: '#25A7D8',
              '&:hover': {
                bgcolor: '#1795c7',
              },
              '&.Mui-disabled': {
                bgcolor: '#9FD7EA',
                color: 'rgba(255,255,255,0.9)',
              },
            }}
          >
            Done
          </Button>
        </Box>
      </Dialog>

      <Dialog
        open={isBlockDatesOpen}
        onClose={closeBlockDatesModal}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
            p: 0,
            maxWidth: '760px',
          },
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2.5, pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
            <Box>
              <Typography sx={{ color: '#1E2A36', fontWeight: 700, fontSize: { xs: '1.16rem', sm: '1.6rem' }, lineHeight: 1 }}>
                Block Dates
              </Typography>
              <Typography sx={{ mt: 1, color: '#687886', fontSize: '0.93rem' }}>
                Select a range - clients cannot book during this time
              </Typography>
            </Box>

            <IconButton
              aria-label="Close block dates modal"
              onClick={closeBlockDatesModal}
              sx={{
                mt: 0.4,
                bgcolor: '#EDF1F5',
                color: '#73818D',
                width: 32,
                height: 32,
                '&:hover': {
                  bgcolor: '#E2E8EF',
                },
              }}
            >
              <X size={18} />
            </IconButton>
          </Box>

          <Box sx={{ mt: 1.8, pb: 2.1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.6 }}>
              <IconButton
                aria-label="Previous month"
                onClick={() => setBlockDatesMonth((prev) => addMonths(prev, -1))}
                sx={{
                  color: '#8A98A6',
                  width: 28,
                  height: 28,
                  '&:hover': {
                    bgcolor: '#F2F6FA',
                  },
                }}
              >
                <ChevronLeft size={18} />
              </IconButton>

              <Typography sx={{ color: '#1E2A36', fontSize: '0.9rem', fontWeight: 700 }}>{format(blockDatesMonth, 'MMMM yyyy')}</Typography>

              <IconButton
                aria-label="Next month"
                onClick={() => setBlockDatesMonth((prev) => addMonths(prev, 1))}
                sx={{
                  color: '#8A98A6',
                  width: 28,
                  height: 28,
                  '&:hover': {
                    bgcolor: '#F2F6FA',
                  },
                }}
              >
                <ChevronRight size={18} />
              </IconButton>
            </Box>

            <Box sx={{ mt: 1.45, display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 0.55 }}>
              {CALENDAR_WEEK_HEADERS.map((weekdayLabel) => (
                <Typography
                  key={weekdayLabel}
                  sx={{
                    textAlign: 'center',
                    color: '#C0CBD6',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                  }}
                >
                  {weekdayLabel}
                </Typography>
              ))}
            </Box>

            <Box sx={{ mt: 0.9, display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 0.45 }}>
              {blockDatesCalendarCells.map((cellDate, cellIndex) => {
                if (!cellDate) {
                  return <Box key={`empty-${cellIndex}`} sx={{ height: 36 }} />;
                }

                const isStart = Boolean(blockStartDate && isSameDay(cellDate, blockStartDate));
                const isEnd = Boolean(blockEndDate && isSameDay(cellDate, blockEndDate));
                const isSelected = isStart || isEnd;
                const isWithinRange = Boolean(
                  blockStartDate &&
                    blockEndDate &&
                    !isSelected &&
                    (isAfter(cellDate, blockStartDate) || isSameDay(cellDate, blockStartDate)) &&
                    (isBefore(cellDate, blockEndDate) || isSameDay(cellDate, blockEndDate))
                );
                const isPastDay = isPastScheduleDay(cellDate);
                const isAlreadyBlockedDay = isBlockedDateAlreadySaved(cellDate);
                const wouldIncludeBlockedDay = Boolean(
                  blockStartDate &&
                    !blockEndDate &&
                    isAfter(cellDate, blockStartDate) &&
                    rangeIntersectsBlockedDateRanges(blockStartDate, cellDate, timeOffEntries),
                );

                if (isPastDay || isAlreadyBlockedDay || wouldIncludeBlockedDay) {
                  return (
                    <Box
                      key={cellDate.toISOString()}
                      aria-label={
                        isAlreadyBlockedDay || wouldIncludeBlockedDay
                          ? `${format(cellDate, 'dd MMM yyyy')} is already blocked`
                          : `${format(cellDate, 'dd MMM yyyy')} is in the past`
                      }
                      sx={{
                        width: '100%',
                        height: 36,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '8px',
                        fontSize: '1.03rem',
                        fontWeight: 500,
                        color: isAlreadyBlockedDay || wouldIncludeBlockedDay ? '#B5A090' : '#D2DCE6',
                        bgcolor: isAlreadyBlockedDay || wouldIncludeBlockedDay ? '#F8EFE8' : 'transparent',
                        cursor: 'not-allowed',
                        textDecoration: isAlreadyBlockedDay ? 'line-through' : 'none',
                      }}
                    >
                      {getDate(cellDate)}
                    </Box>
                  );
                }

                return (
                  <Button
                    key={cellDate.toISOString()}
                    type="button"
                    onClick={() => handleCalendarDateSelect(cellDate)}
                    sx={{
                      minWidth: 0,
                      width: '100%',
                      height: 36,
                      borderRadius: '8px',
                      p: 0,
                      fontSize: '1.03rem',
                      fontWeight: 500,
                      textTransform: 'none',
                      color: isSelected ? '#fff' : '#1E2A36',
                      bgcolor: isSelected ? '#25A7D8' : isWithinRange ? '#EAF7FD' : 'transparent',
                      '&:hover': {
                        bgcolor: isSelected ? '#1795c7' : isWithinRange ? '#DDF1FB' : '#F3F8FC',
                      },
                    }}
                  >
                    {getDate(cellDate)}
                  </Button>
                );
              })}
            </Box>

            <Typography sx={{ color: '#1E2A36', fontSize: '0.77rem', fontWeight: 700, mt: 1.75, mb: 0.8 }}>Select Date</Typography>
            {blockDatesInlineError ? (
              <Box
                role="alert"
                sx={{
                  mb: 1,
                  border: '1px solid #F0B5B5',
                  borderRadius: '10px',
                  bgcolor: '#FFF7F7',
                  px: 1.4,
                  py: 1,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                }}
              >
                <AlertCircle size={16} color="#C45C5C" style={{ marginTop: 2, flexShrink: 0 }} />
                <Typography sx={{ color: '#915252', fontSize: '0.83rem', lineHeight: 1.4 }}>
                  {blockDatesInlineError}
                </Typography>
              </Box>
            ) : null}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'minmax(0,150px) 24px minmax(0,150px)' },
                  alignItems: 'center',
                  columnGap: 1.2,
                  rowGap: 1,
                  maxWidth: 350,
                }}
              >
                <DatePicker
                  format="DD/MM/YYYY"
                  value={blockStartDate ? dayjs(blockStartDate) : null}
                  minDate={blockDatesMinDay}
                  shouldDisableDate={shouldDisableBlockStartDate}
                  disabled={isSavingTimeOff}
                  onChange={handleBlockStartDatePickerChange}
                  slots={{
                    openPickerIcon: () => (
                      <Box component="img" src={appointmentDateIcon} alt="" sx={{ width: 20, height: 20, display: 'block' }} />
                    ),
                  }}
                  slotProps={{
                    textField: {
                      required: true,
                      placeholder: 'DD/MM/YYYY',
                      error: Boolean(blockDatesInlineError),
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          height: 40,
                          borderRadius: '9px',
                          '& fieldset': {
                            borderColor: blockDatesInlineError ? '#E5A0A0' : '#D4DEE7',
                          },
                        },
                        '& .MuiOutlinedInput-input': {
                          px: 1.2,
                          py: 1.1,
                          fontSize: '0.92rem',
                        },
                      },
                    },
                  }}
                />
                <Typography sx={{ color: '#667380', fontSize: '1rem', textAlign: 'center', display: { xs: 'none', sm: 'block' } }}>-</Typography>
                <DatePicker
                  format="DD/MM/YYYY"
                  value={blockEndDate ? dayjs(blockEndDate) : null}
                  minDate={blockEndDateMinDay}
                  shouldDisableDate={shouldDisableBlockEndDate}
                  disabled={isSavingTimeOff || !blockStartDate}
                  onChange={handleBlockEndDatePickerChange}
                  slots={{
                    openPickerIcon: () => (
                      <Box component="img" src={appointmentDateIcon} alt="" sx={{ width: 20, height: 20, display: 'block' }} />
                    ),
                  }}
                  slotProps={{
                    textField: {
                      placeholder: 'DD/MM/YYYY',
                      error: Boolean(blockDatesInlineError),
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          height: 40,
                          borderRadius: '9px',
                          '& fieldset': {
                            borderColor: blockDatesInlineError ? '#E5A0A0' : '#D4DEE7',
                          },
                        },
                        '& .MuiOutlinedInput-input': {
                          px: 1.2,
                          py: 1.1,
                          fontSize: '0.92rem',
                        },
                      },
                    },
                  }}
                />
              </Box>
            </LocalizationProvider>

            <TextField
              fullWidth
              type="text"
              disabled={isSavingTimeOff}
              label="Reason (optional)"
              placeholder="Eg. Annual Leave"
              value={blockReason}
              onChange={(event) => setBlockReason(event.target.value)}
              sx={{
                mt: 1.7,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '& fieldset': {
                    borderColor: '#D4DEE7',
                  },
                  '&:hover fieldset': {
                    borderColor: '#BCCBD7',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#52B2D7',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  px: 1.5,
                  py: 1.2,
                  fontSize: '0.96rem',
                },
                '& .MuiInputLabel-root': {
                  fontFamily: 'Inter, system-ui, sans-serif',
                  fontWeight: 400,
                  fontSize: '0.75rem',
                  lineHeight: '12px',
                  letterSpacing: '0.15px',
                  verticalAlign: 'middle',
                  color: '#7B8A96',
                  leadingTrim: 'none',
                },
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            borderTop: '1px solid #E4ECF2',
            bgcolor: '#F9FBFD',
            px: { xs: 2, sm: 3 },
            py: 1.8,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            type="button"
            variant="contained"
            disableElevation
            onClick={() => void addTimeOffEntry()}
            disabled={!blockStartDate || isSavingTimeOff || Boolean(blockDatesValidationError)}
            sx={{
              minWidth: 88,
              height: 44,
              borderRadius: '14px',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '1.1rem',
              bgcolor: '#25A7D8',
              '&:hover': {
                bgcolor: '#1795c7',
              },
              '&.Mui-disabled': {
                bgcolor: '#9FD7EA',
                color: 'rgba(255,255,255,0.9)',
              },
            }}
          >
            {isSavingTimeOff ? 'Saving...' : 'Done'}
          </Button>
        </Box>
      </Dialog>

      <Dialog
        open={isAppointmentConflictOpen}
        onClose={closeAppointmentConflictModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
            p: 0,
            maxWidth: '560px',
          },
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2.5, pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: '#1E2A36', fontWeight: 700, fontSize: { xs: '1.16rem', sm: '1.35rem' }, lineHeight: 1 }}>
                Appointments are scheduled
              </Typography>
              <Typography sx={{ mt: 1, color: '#687886', fontSize: '0.93rem', lineHeight: 1.45 }}>
                Clients cannot book during this blocked period until existing sessions are handled.
              </Typography>
            </Box>

            <IconButton
              aria-label="Close appointment conflict modal"
              onClick={closeAppointmentConflictModal}
              sx={{
                mt: 0.4,
                bgcolor: '#EDF1F5',
                color: '#73818D',
                width: 32,
                height: 32,
                flexShrink: 0,
                '&:hover': {
                  bgcolor: '#E2E8EF',
                },
              }}
            >
              <X size={18} />
            </IconButton>
          </Box>

          <Box
            sx={{
              mt: 1.9,
              mb: 2.1,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.1,
              border: '1px solid #F0D4A8',
              borderRadius: '10px',
              bgcolor: '#FFFAF3',
              py: 1.15,
              px: 1.35,
            }}
          >
            <AlertCircle size={18} color="#E09B2D" style={{ marginTop: 2, flexShrink: 0 }} />
            <Typography sx={{ color: '#5E4A2E', fontSize: '0.9rem', lineHeight: 1.45 }}>
              You have {formatAppointmentCount(visibleConflictCount)} during this time. Cancel them or reschedule each
              session before continuing.
            </Typography>
          </Box>
          {timeOffConflictError ? (
            <Box sx={{ mb: 2.1 }}>
              <InlineFeedback
                variant="error"
                title="Unable to continue"
                message={timeOffConflictError}
              />
            </Box>
          ) : null}
        </Box>

        <Box
          sx={{
            borderTop: '1px solid #E4ECF2',
            bgcolor: '#F9FBFD',
            px: { xs: 2, sm: 3 },
            py: 1.8,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            gap: 1.2,
          }}
        >
          <Button
            type="button"
            variant="contained"
            disableElevation
            onClick={resolveAppointmentConflictByRescheduling}
            sx={{
              width: '100%',
              minWidth: 0,
              height: 44,
              borderRadius: '12px',
              px: 1.4,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.9rem',
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              gridColumn: { sm: 2 },
              bgcolor: '#25A7D8',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: '#1795c7',
                boxShadow: 'none',
              },
            }}
          >
            Reschedule Appointments
          </Button>
          <Button
            type="button"
            variant="outlined"
            onClick={resolveAppointmentConflictByCancelling}
            sx={{
              width: '100%',
              minWidth: 0,
              height: 44,
              borderRadius: '12px',
              px: 1.4,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              gridColumn: { sm: 1 },
              gridRow: { sm: 1 },
              color: '#3E4B58',
              borderColor: '#CCD6E0',
              bgcolor: '#fff',
              '&:hover': {
                borderColor: '#B8C7D6',
                bgcolor: '#F9FBFD',
              },
            }}
          >
            Cancel
          </Button>
        </Box>
      </Dialog>

      <Dialog
        open={isRescheduleSessionOpen}
        onClose={closeRescheduleSessionModal}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
            p: 0,
            maxWidth: '760px',
          },
        }}
      >
        <Box sx={{ px: 2.5, pt: 1.9, pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.1 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '12px',
                  bgcolor: '#FCECD7',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mt: 0.2,
                }}
              >
                <CalendarDays size={17} color="#F2A93C" />
              </Box>

	              <Box>
	                <Typography sx={{ color: '#131E29', fontSize: '1.35rem', fontWeight: 700, lineHeight: 1 }}>Reschedule Session</Typography>
	                <Typography sx={{ mt: 0.8, color: '#707B87', fontSize: '0.84rem' }}>
	                  You have {formatAppointmentCount(visibleConflictCount)} to reschedule due to time off.
	                </Typography>
	              </Box>
            </Box>

            <IconButton
              aria-label="Close reschedule session modal"
              onClick={closeRescheduleSessionModal}
              sx={{
                bgcolor: '#EDF1F5',
                color: '#73818D',
                width: 32,
                height: 32,
                '&:hover': {
                  bgcolor: '#E2E8EF',
                },
              }}
            >
              <X size={18} />
            </IconButton>
          </Box>

          {timeOffConflictError ? (
            <Box sx={{ mt: 1.6 }}>
              <InlineFeedback
                variant="error"
                title="Unable to update appointment"
                message={timeOffConflictError}
              />
            </Box>
          ) : null}

          <Box sx={{ mt: 1.8, borderTop: '1px solid #E8EEF4', pt: 1.9, pb: 1.8 }}>
            {rescheduleAppointments.map((appointment) => (
                <Box
                  key={appointment.id}
                  sx={{
                    border: '1px solid #DFE7EE',
                    borderRadius: '14px',
                    bgcolor: '#fff',
                    px: 1.8,
                    py: 1.45,
                    mb: 1.4,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                      <Box sx={{ minWidth: 64, borderRight: '1px solid #EDF2F7', pr: 1.25 }}>
                        <Typography sx={{ color: '#353B44', fontSize: '1.45rem', fontWeight: 700, lineHeight: 1 }}>
                          {appointment.day}
                        </Typography>
                        <Typography sx={{ mt: 0.15, color: '#828C97', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.04em' }}>
                          {appointment.month}
                        </Typography>
                      </Box>

                      <Box sx={{ pl: 1.25, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.8 }}>
                          <Typography sx={{ color: '#1C2733', fontSize: '1.05rem', fontWeight: 700 }}>
                            <ClientNameLink clientId={appointment.clientId || appointment.appointment?.clientId} className="text-[#1C2733] hover:text-[#1C2733]">
                              {appointment.clientName}
                            </ClientNameLink>
                          </Typography>
                          <Typography sx={{ color: '#00AFA1', fontSize: '1.05rem', fontWeight: 700 }}>{appointment.title}</Typography>
                        </Box>
                        <Typography sx={{ mt: 0.35, color: '#8A95A1', fontSize: '1.02rem' }}>{appointment.meta}</Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.9, pl: 1 }}>
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={() => {
                          openAppointmentRescheduleDialog(appointment);
                        }}
                        disabled={isSavingTimeOff || cancellingRescheduleId === appointment.id}
                        sx={{
                          height: 36,
                          minWidth: 96,
                          borderRadius: '10px',
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '0.86rem',
                          color: '#4B5967',
                          borderColor: '#CCD6E0',
                          '&.Mui-disabled': {
                            color: '#8A95A1',
                            borderColor: '#D7E1EA',
                          },
                          '&:hover': {
                            borderColor: '#B8C7D6',
                            bgcolor: '#F9FBFD',
                          },
                        }}
                      >
                        Reschedule
                      </Button>
                      <Button
                        type="button"
                        onClick={() => {
                          void cancelRescheduleAppointment(appointment.id);
                        }}
                        disabled={isSavingTimeOff || cancellingRescheduleId === appointment.id}
                        sx={{
                          minWidth: 0,
                          px: 0.35,
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '0.86rem',
                          color: '#FA5F5F',
                          '&.Mui-disabled': {
                            color: '#F8B4B4',
                          },
                          '&:hover': {
                            bgcolor: 'transparent',
                            color: '#F04343',
                          },
                        }}
                      >
                        {cancellingRescheduleId === appointment.id ? 'Cancelling...' : 'Cancel'}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              ))}
          </Box>
        </Box>
      </Dialog>

      <ScheduleAppointmentDialog
        open={isScheduleAppointmentOpen}
        onClose={closeAppointmentRescheduleDialog}
        appointmentIdToEdit={rescheduleAppointmentId}
        blockedDateRanges={
          pendingTimeOffPayload
            ? [{ startDate: pendingTimeOffPayload.startDate, endDate: pendingTimeOffPayload.endDate }]
            : []
        }
        onAppointmentUpdated={handleAppointmentRescheduled}
      />
    </Box>
  );
};

export default AvailabilityView;
